import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import { transcriptApi } from '../../api/transcriptApi';
import Notification from '../../components/ui/Notification';
import { ClockIcon, ArrowTopRightOnSquareIcon, PlayIcon, PencilSquareIcon, TrashIcon, ArrowPathIcon, ArrowDownTrayIcon, SparklesIcon, DocumentTextIcon, ChevronDownIcon, ChevronUpIcon, Cog6ToothIcon, CheckCircleIcon, InformationCircleIcon, WrenchScrewdriverIcon, LanguageIcon, XMarkIcon } from '@heroicons/react/24/outline';
import TranscriptStatusDisplay from '../../components/reusable/TranscriptStatusDisplay';
import ReportStatusDisplay from '../../components/reusable/ReportStatusDisplay';
import TranscriptDisplay from '../../components/reusable/TranscriptDisplay';
import ReadMoreText from '../../components/reusable/ReadMoreText';
import { TRANSCRIPT_PROVIDERS, DEFAULT_PROVIDER } from '../../constants/transcriptProviders';
import { formatDuration } from '../../utils/formatDuration';
import { reportApi } from '../../api/reportApi';
import { apiClient } from '../../api/http.api';
import ReportConfigModal from '../../components/reusable/ReportConfigModal';
import PublishModal from '../../components/reusable/PublishModal';
import { getDefaultModel } from '../../config/reportPrompts';
import { label as tLabel } from '../../config/reportLabelTranslations';
import './session-detail.css';

const LANGUAGE_NAMES = {
  en: 'English', es: 'Spanish', fr: 'French', it: 'Italian', de: 'German',
  pt: 'Portuguese', ar: 'Arabic', zh: 'Chinese', ja: 'Japanese', ko: 'Korean',
  ru: 'Russian', tr: 'Turkish', nl: 'Dutch', pl: 'Polish', sv: 'Swedish',
  sr: 'Serbian', hr: 'Croatian', bs: 'Bosnian',
};

const formatReportModelLabel = (model) => {
  const labels = {
    'openai/gpt-oss-120b': 'GPT-OSS 120B (Local)',
    'gpt-5.4': 'GPT-5.4',
    'gpt-4o': 'GPT-4o',
    'claude-sonnet-4-6': 'Claude Sonnet 4.6 (disabled)',
    'claude-sonnet-4-20250514': 'Claude Sonnet 4 (legacy)',
  };
  return labels[model] || model;
};

function SourceReferencesPanel({ sourceReferences }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const entries = Object.entries(sourceReferences || {});
  if (entries.length === 0) return null;
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-3 border-b border-gray-200 flex items-center justify-between hover:from-gray-100 hover:to-gray-150/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span className="text-sm font-medium text-gray-700">External Sources ({entries.length})</span>
        </div>
        <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      {isExpanded && (
        <div className="p-4 space-y-2">
          {entries.map(([num, ref]) => (
            <div key={num} className="flex items-start gap-2 text-xs">
              <span className="inline-flex items-center justify-center font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1 py-0.5 shrink-0" style={{ fontSize: '10px', minWidth: '28px' }}>
                S{num}
              </span>
              <div className="min-w-0">
                {ref.url ? (
                  <a href={ref.url} target="_blank" rel="noopener noreferrer" className="font-medium text-teal-700 hover:underline">
                    {ref.title || ref.url}
                  </a>
                ) : (
                  <span className="font-medium text-gray-700">{ref.title || 'Untitled'}</span>
                )}
                {ref.snippet && (
                  <span className="text-gray-500 ml-1">&mdash; {ref.snippet.substring(0, 120)}{ref.snippet.length > 120 ? '...' : ''}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SessionDetailNewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading, error, getAuth } = useAuth();
  const [data, setData] = useState(null);
  const [notif, setNotif] = useState(null);
  const [mdSessionAtGlance, setMdSessionAtGlance] = useState('');
  const [mdFullReport, setMdFullReport] = useState('');
  const [mdTranscript, setMdTranscript] = useState(''); // report tab: Session transcript (summary)
  const [mdSpeakers, setMdSpeakers] = useState('');
  const [mdInDepth, setMdInDepth] = useState('');
  const [knowledgeGraphUrl, setKnowledgeGraphUrl] = useState('https://example.com/graph');
  const [activeTab, setActiveTab] = useState('transcript'); // 'transcript' | 'report' | 'speakers' | 'details'
  const [autoRefresh, setAutoRefresh] = useState(true); // Auto-refresh toggle for transcript processing
  const [selectedProvider, setSelectedProvider] = useState(DEFAULT_PROVIDER);
  const [isGenerating, setIsGenerating] = useState(false);
  const [transcriptSegments, setTranscriptSegments] = useState([]);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);
  const [isLoadingReportStatus, setIsLoadingReportStatus] = useState(false);
  const [expandedSpeakers, setExpandedSpeakers] = useState(new Set());
  const [expandedChunks, setExpandedChunks] = useState(new Set());
  const [autoRefreshReport, setAutoRefreshReport] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [showReportConfigModal, setShowReportConfigModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishDestination, setPublishDestination] = useState(null); // 'faicon' | 'wordpress'
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [transcriptReferences, setTranscriptReferences] = useState(null);
  const [sourceReferences, setSourceReferences] = useState(null);
  const [relatedResources, setRelatedResources] = useState([]);
  const [factualNotes, setFactualNotes] = useState([]);
  const [highlightSentence, setHighlightSentence] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const transcriptContainerRef = useRef(null);

  const toggleSection = useCallback((key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleCitationClick = useCallback((startNum, endNum) => {
    const end = endNum || startNum;
    const savedScrollY = window.scrollY;
    setHighlightSentence({ start: startNum, end });
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        const sentEl = document.getElementById(`ref-${startNum}`);
        if (sentEl) {
          sentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 400);
    }
    setTimeout(() => {
      setHighlightSentence(null);
      window.scrollTo({ top: savedScrollY, behavior: 'smooth' });
    }, 5000);
  }, []);

  // Report language state
  const [reportLang, setReportLang] = useState('en');
  const [reportLanguages, setReportLanguages] = useState([]);
  const [isTranslatingReport, setIsTranslatingReport] = useState(false);
  const [translatingLangCode, setTranslatingLangCode] = useState(null);
  const [showReportLangMenu, setShowReportLangMenu] = useState(false);
  const reportLangMenuRef = useRef(null);
  const translationPollRef = useRef(null);

  // Fix Missing Sections state
  const [showFixSectionsModal, setShowFixSectionsModal] = useState(false);
  const [detectedGaps, setDetectedGaps] = useState(null);
  const [isDetectingGaps, setIsDetectingGaps] = useState(false);
  const [regeneratingSectionIdx, setRegeneratingSectionIdx] = useState(null);
  const [isRegeneratingAll, setIsRegeneratingAll] = useState(false);

  // Media URL for "View" button.
  // Important: Some API endpoints return Content-Disposition: attachment (forces download).
  // For "View" we prefer direct/public file URLs (like `file_url` / `video_url`) that browsers can stream inline.
  const fullMediaUrl = useMemo(() => {
    if (!data) return null;

    const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://reporting-api.diplomacy.edu';

    // Prefer URLs that are meant to be opened in browser (same as list links)
    // Note: `normalizeData` already maps `file_url`, but keep camelCase fallbacks too.
    let fileUrl = data.file_url || data.fileUrl || data.video_url || data.videoUrl;

    // If backend provides the session-video endpoint as file_url, ignore it for "View"
    // because it typically forces download.
    const isSessionVideoEndpoint =
      typeof fileUrl === 'string' && /\/api\/reports\/sessions\/[^/]+\/video\b/.test(fileUrl);
    if (isSessionVideoEndpoint) {
      fileUrl = null;
    }

    // If we don't have a direct URL, use the same fallback as sessions list cards:
    // original_file_id -> /api/files/:id/content
    if (!fileUrl && data.original_file_id) {
      fileUrl = `/api/files/${data.original_file_id}/content`;
    }

    // Intentionally DO NOT fall back to `/api/reports/sessions/:id/video` for View,
    // because that endpoint often forces download via Content-Disposition: attachment.

    // Construct full URL if relative
    const fullUrl = fileUrl && !fileUrl.startsWith('http') ? `${apiBase}${fileUrl}` : fileUrl;
    return fullUrl || null;
  }, [data, id]);

  const handleViewMedia = useCallback(() => {
    if (!fullMediaUrl) return;
    // Open exactly like sessions list does (anchor in a new tab)
    const a = document.createElement('a');
    a.href = fullMediaUrl;
    a.target = '_blank';
    a.rel = 'noreferrer';
    a.click();
  }, [fullMediaUrl]);

  const [isDownloadingTranscriptPdf, setIsDownloadingTranscriptPdf] = useState(false);
  const handleDownloadTranscriptPdf = useCallback(async () => {
    if (!transcriptSegments || transcriptSegments.length === 0) return;
    setIsDownloadingTranscriptPdf(true);
    try {
      const { exportTranscript } = await import('../../components/TranscriptEditor/utils/exportFormats');
      const blocks = transcriptSegments.map((segment, index) => ({
        id: segment.id || `segment-${index}`,
        text: segment.text || segment.content || '',
        speaker: segment.speaker || segment.speaker_name || segment.speakerName || 'UNKNOWN',
        start: segment.start || segment.start_time || 0,
        end: segment.end || segment.end_time || 0,
        words: segment.words || [],
        paragraphNumber: segment.paragraph_number || index,
      }));
      const title = data?.title || 'Transcript';
      exportTranscript(blocks, 'pdf', title);
    } catch (err) {
      console.error('Transcript PDF export failed:', err);
      setNotif({ type: 'error', title: 'Download failed', message: err.message || 'Could not export transcript to PDF.' });
    } finally {
      setIsDownloadingTranscriptPdf(false);
    }
  }, [transcriptSegments, data]);

  // Normalize backend camelCase to snake_case
  const normalizeData = (session) => ({
    ...session,
    id: session.id,
    user_id: session.userId || session.user_id,
    user_name: session.userName || session.user_name,
    user_email: session.userEmail || session.user_email,
    title: session.title,
    description: session.description,
    status: session.status,
    original_file_id: session.originalFileId || session.original_file_id,
    original_filename: session.originalFilename || session.original_filename,
    storage_path: session.storagePath || session.storage_path,
    processing_started_at: session.processingStartedAt || session.processing_started_at,
    processing_completed_at: session.processingCompletedAt || session.processing_completed_at,
    error_message: session.errorMessage || session.error_message,
    event_id: session.eventId || session.event_id,
    event_title: session.eventTitle || session.event_title,
    event_wp_id: session.eventWpId || session.event_wp_id,
    event_faicon_id: session.eventFaiconId || session.event_faicon_id,
    wp_session_id: session.wpSessionId || session.wp_session_id,
    faicon_session_id: session.faiconSessionId || session.faicon_session_id,
    url: session.url,
    created_at: session.createdAt || session.created_at,
    updated_at: session.updatedAt || session.updated_at,
    has_transcripts: session.hasTranscripts || session.has_transcripts || false,
    has_reports: session.hasReports || session.has_reports || false,
    transcript_count: session.transcriptCount || session.transcript_count || 0,
    report_count: session.reportCount || session.report_count || 0,
    transcript_model: session.transcriptModel || session.transcript_model,
    file_url: session.fileUrl || session.file_url,
    duration_seconds: session.durationSeconds || session.duration_seconds,
    // Transcript-specific fields
    transcript_status: session.transcriptStatus || session.transcript_status || session.status,
    transcript_processing_started_at: session.transcriptProcessingStartedAt || session.transcript_processing_started_at,
    transcript_processing_completed_at: session.transcriptProcessingCompletedAt || session.transcript_processing_completed_at,
    transcript_processing_duration: session.transcriptProcessingDuration || session.transcript_processing_duration,
    transcript_error_message: session.transcriptErrorMessage || session.transcript_error_message,
    transcript_provider: session.transcriptProvider || session.transcript_provider,
    // Chunking progress fields
    transcript_chunks_total: session.transcriptChunksTotal || session.transcript_chunks_total,
    transcript_chunks_completed: session.transcriptChunksCompleted || session.transcript_chunks_completed,
    transcript_current_chunk: session.transcriptCurrentChunk || session.transcript_current_chunk,
  });

  // Load transcript segments
  const loadTranscriptData = useCallback(async () => {
    console.log('[loadTranscriptData] Called with data:', {
      hasData: !!data,
      transcript_status: data?.transcript_status,
      id
    });
    
    // Load transcript if status is PROCESSING or COMPLETED (progressive delivery)
    if (!data || (data.transcript_status !== 'COMPLETED' && data.transcript_status !== 'PROCESSING')) {
      console.log('[loadTranscriptData] Skipping - status not COMPLETED or PROCESSING:', data?.transcript_status);
      setTranscriptSegments([]);
      return;
    }

    try {
      console.log('[loadTranscriptData] Loading transcript for session:', id);
      setLoadingTranscript(true);
      const transcriptData = await transcriptApi.getTranscript(id);
      
      console.log('[loadTranscriptData] Received transcript data:', {
        hasSegments: !!transcriptData.segments,
        segmentCount: transcriptData.segments?.length,
        keys: Object.keys(transcriptData)
      });
      
      // Handle different response formats
      let segments = [];
      if (transcriptData.segments) {
        segments = transcriptData.segments;
      } else if (transcriptData.transcript?.segments) {
        segments = transcriptData.transcript.segments;
      } else if (Array.isArray(transcriptData)) {
        segments = transcriptData;
      }
      
      console.log('[loadTranscriptData] Setting segments:', segments.length);
      setTranscriptSegments(segments);
    } catch (e) {
      console.error('[loadTranscriptData] Failed to load transcript:', e);
      // Don't show error notification - transcript might just not exist yet
      setTranscriptSegments([]);
    } finally {
      setLoadingTranscript(false);
    }
  }, [id, data?.transcript_status]);

  // Load session data
  const loadSessionData = useCallback(async () => {
    try {
      const res = await transcriptApi.getSessionById(id, { compact: true });
      if (!res || Object.keys(res).length === 0) {
        setNotif({ type: 'error', title: 'Session unavailable', message: 'The backend returned an empty session response.' });
        setData(null);
      } else {
        const normalized = normalizeData(res);
        setData(normalized);
        
        // ALWAYS use markdown fields from session object (source of truth)
        // Even if empty, these take priority over report file data
        // Check both snake_case and camelCase field names
        const sessionAtGlance = res.session_at_glance_md ?? res.sessionAtGlanceMd ?? '';
        const fullReport = res.full_report_md ?? res.fullReportMd ?? '';
        const transcript = res.transcript_md ?? res.transcriptMd ?? '';
        const speakers = res.speakers_md ?? res.speakersMd ?? '';
        const inDepth = res.in_depth_md ?? res.inDepthMd ?? '';
        
        // Set markdown fields immediately - these are the source of truth
        setMdSessionAtGlance(sessionAtGlance);
        setMdFullReport(fullReport);
        setMdTranscript(transcript);
        setMdSpeakers(speakers);
        setMdInDepth(inDepth);
        setKnowledgeGraphUrl(res.knowledge_graph_url || res.knowledgeGraphUrl || knowledgeGraphUrl);
        
        console.log('[loadSessionData] Loaded markdown fields from session:', {
          sessionAtGlance: sessionAtGlance ? `length: ${sessionAtGlance.length}` : 'empty',
          fullReport: fullReport ? `length: ${fullReport.length}` : 'empty',
          speakers: speakers ? `length: ${speakers.length}` : 'empty',
          inDepth: inDepth ? `length: ${inDepth.length}` : 'empty',
        });
      }
    } catch (e) {
      setNotif({ type: 'error', title: 'Failed to load session', message: e.message });
      setData(null);
    }
  }, [id, knowledgeGraphUrl]);

  // Load available report languages — auto-resume polling if a translation is in progress
  const loadReportLanguages = useCallback(async () => {
    try {
      const res = await reportApi.getReportLanguages(id);
      const langs = res.languages || [];
      setReportLanguages(langs);
      const inProgress = langs.find(l => l.status === 'PROCESSING' || l.status === 'PENDING');
      if (inProgress && !translationPollRef.current) {
        setIsTranslatingReport(true);
        setTranslatingLangCode(inProgress.code);
        const pollInterval = setInterval(async () => {
          try {
            const r = await reportApi.getReportLanguages(id);
            const ls = r.languages || [];
            setReportLanguages(ls);
            const target = ls.find(l => l.code === inProgress.code);
            if (target && (target.status === 'COMPLETED' || target.status === 'FAILED')) {
              clearInterval(pollInterval);
              translationPollRef.current = null;
              setIsTranslatingReport(false);
              setTranslatingLangCode(null);
            }
          } catch {
            clearInterval(pollInterval);
            translationPollRef.current = null;
            setIsTranslatingReport(false);
            setTranslatingLangCode(null);
          }
        }, 5000);
        translationPollRef.current = pollInterval;
      }
    } catch (e) {
      console.warn('Failed to load report languages:', e);
    }
  }, [id]);

  // Switch report language — re-fetches session data with ?lang=XX
  const handleReportLangSwitch = useCallback(async (lang) => {
    if (lang === reportLang) return;
    try {
      if (lang === 'en') {
        await loadSessionData();
      } else {
        const res = await apiClient.get(`/api/reports/sessions/${id}`, { params: { lang } });
        const d = res.data;
        setMdSessionAtGlance(d.session_at_glance_md ?? d.sessionAtGlanceMd ?? '');
        setMdFullReport(d.full_report_md ?? d.fullReportMd ?? '');
        setMdSpeakers(d.speakers_md ?? d.speakersMd ?? '');
        setMdInDepth(d.in_depth_md ?? d.inDepthMd ?? '');
      }
      setReportLang(lang);
    } catch (e) {
      console.error('Error switching report language:', e);
      setNotif({ type: 'error', title: 'Language switch failed', message: e.message });
    }
  }, [id, reportLang, loadSessionData]);

  // Trigger report translation
  const handleTranslateReport = useCallback(async (lang) => {
    if (!lang) return;
    try {
      setIsTranslatingReport(true);
      setTranslatingLangCode(lang);
      await reportApi.translateReport(id, lang);
      setNotif({ type: 'success', title: 'Translation started', message: `Report translation to ${LANGUAGE_NAMES[lang] || lang} has started. This may take a minute.` });
      const pollInterval = setInterval(async () => {
        try {
          const res = await reportApi.getReportLanguages(id);
          const langs = res.languages || [];
          setReportLanguages(langs);
          const target = langs.find(l => l.code === lang);
          if (target && (target.status === 'COMPLETED' || target.status === 'FAILED')) {
            clearInterval(pollInterval);
            translationPollRef.current = null;
            setIsTranslatingReport(false);
            setTranslatingLangCode(null);
            if (target.status === 'COMPLETED') {
              setNotif({ type: 'success', title: 'Translation complete', message: `Report translated to ${LANGUAGE_NAMES[lang] || lang}` });
            } else {
              setNotif({ type: 'error', title: 'Translation failed', message: target.error_message || 'Unknown error' });
            }
          }
        } catch {
          clearInterval(pollInterval);
          translationPollRef.current = null;
          setIsTranslatingReport(false);
          setTranslatingLangCode(null);
        }
      }, 5000);
      translationPollRef.current = pollInterval;
    } catch (e) {
      setIsTranslatingReport(false);
      setTranslatingLangCode(null);
      setNotif({ type: 'error', title: 'Translation failed', message: e.response?.data?.detail || e.message });
    }
  }, [id]);

  const handleCancelTranslation = useCallback(async (lang) => {
    if (!lang) return;
    try {
      await reportApi.cancelTranslation(id, lang);
      if (translationPollRef.current) {
        clearInterval(translationPollRef.current);
        translationPollRef.current = null;
      }
      setIsTranslatingReport(false);
      setTranslatingLangCode(null);
      await loadReportLanguages();
      setNotif({ type: 'success', title: 'Translation cancelled', message: `Translation to ${LANGUAGE_NAMES[lang] || lang} was cancelled.` });
    } catch (e) {
      setNotif({ type: 'error', title: 'Cancel failed', message: e.response?.data?.detail || e.message });
    }
  }, [id, loadReportLanguages]);

  const handleDeleteTranslation = useCallback(async (lang) => {
    if (!lang || lang === 'en') return;
    try {
      await reportApi.deleteTranslation(id, lang);
      if (reportLang === lang) {
        await handleReportLangSwitch('en');
      }
      await loadReportLanguages();
      setNotif({ type: 'success', title: 'Translation deleted', message: `${LANGUAGE_NAMES[lang] || lang} translation removed.` });
    } catch (e) {
      setNotif({ type: 'error', title: 'Delete failed', message: e.response?.data?.detail || e.message });
    }
  }, [id, reportLang, handleReportLangSwitch, loadReportLanguages]);

  // Initial load
  useEffect(() => {
    if (!user) return;
    // Load session data first, then report status
    // This ensures markdown fields from session are loaded before report status tries to populate them
    loadSessionData().then(() => {
      setTimeout(() => {
        loadReportStatus();
      }, 100);
    });
    loadReportLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]); // Removed loadSessionData from deps to prevent infinite loop

  // Close report language menu on outside click
  useEffect(() => {
    if (!showReportLangMenu) return;
    const handler = (e) => {
      if (reportLangMenuRef.current && !reportLangMenuRef.current.contains(e.target)) {
        setShowReportLangMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showReportLangMenu]);

  // Load transcript when session data changes and transcript is completed
  useEffect(() => {
    if (!user || !data) return;
    loadTranscriptData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, data?.id, data?.transcript_status]); // Use specific data fields instead of entire data object

  // Track previous chunks_completed to detect changes
  const prevChunksCompleted = useRef(null);

  // Optimized polling: use lightweight chunk progress endpoint
  useEffect(() => {
    if (!autoRefresh || !data || data.transcript_status !== 'PROCESSING') {
      prevChunksCompleted.current = null;
      return;
    }
    
    // Initialize previous value
    if (prevChunksCompleted.current === null) {
      prevChunksCompleted.current = data.transcript_chunks_completed || 0;
    }
    
    const interval = setInterval(async () => {
      try {
        // Poll lightweight chunk progress endpoint
        const progress = await transcriptApi.getChunkProgress(id);
        
        // Update data with progress info
        setData(prev => ({
          ...prev,
          transcript_status: progress.transcript_status,
          transcript_chunks_total: progress.chunks_total,
          transcript_chunks_completed: progress.chunks_completed,
          transcript_current_chunk: progress.current_chunk,
          transcript_active_chunks: progress.active_chunks || [],
        }));
        
        // If chunks_completed changed, refresh transcript data
        if (progress.chunks_completed !== prevChunksCompleted.current) {
          prevChunksCompleted.current = progress.chunks_completed;
          loadTranscriptData();
        }
        
        // If status changed to COMPLETED, stop polling and refresh session data
        if (progress.transcript_status === 'COMPLETED') {
          loadSessionData();
        }
      } catch (e) {
        console.error('[Chunk Progress Polling] Error:', e);
        // Fallback to full session data load on error
        loadSessionData();
      }
    }, 3000); // Poll every 3 seconds (more frequent but lightweight)
    
    return () => clearInterval(interval);
  }, [autoRefresh, data?.transcript_status, id, loadTranscriptData, loadSessionData]);

  // Calculate elapsed time for transcript processing (total time from start)
  const [chunkElapsedTime, setChunkElapsedTime] = useState(null);
  useEffect(() => {
    // Show elapsed time if processing or completed (but only if we have a start time)
    if (!data?.transcript_processing_started_at || 
        (data?.transcript_status !== 'PROCESSING' && data?.transcript_status !== 'COMPLETED')) {
      setChunkElapsedTime(null);
      return;
    }

    const updateElapsed = () => {
      // If completed, use completion time, otherwise use current time
      const endTime = data?.transcript_status === 'COMPLETED' && data?.transcript_processing_completed_at
        ? new Date(data.transcript_processing_completed_at)
        : new Date();
      
      const start = new Date(data.transcript_processing_started_at);
      const seconds = Math.floor((endTime - start) / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      
      if (minutes === 0) {
        setChunkElapsedTime(`${remainingSeconds}sec`);
      } else if (remainingSeconds === 0) {
        setChunkElapsedTime(`${minutes}min`);
      } else {
        setChunkElapsedTime(`${minutes}min ${remainingSeconds}sec`);
      }
    };

    updateElapsed();
    
    // Only update interval if still processing (not completed)
    if (data?.transcript_status === 'PROCESSING') {
      const interval = setInterval(updateElapsed, 1000);
      return () => clearInterval(interval);
    }
  }, [data?.transcript_status, data?.transcript_processing_started_at, data?.transcript_processing_completed_at]);

  // Calculate estimated time remaining
  const estimatedTimeRemaining = useMemo(() => {
    if (!data?.transcript_current_chunk || !data?.transcript_chunks_completed || data.transcript_chunks_completed === 0 || !data?.transcript_processing_started_at) {
      return null;
    }
    const elapsed = Math.floor((new Date() - new Date(data.transcript_processing_started_at)) / 1000);
    const avgTimePerChunk = elapsed / data.transcript_chunks_completed;
    const remainingChunks = data.transcript_chunks_total - data.transcript_chunks_completed;
    return formatDuration(Math.round(avgTimePerChunk * remainingChunks));
  }, [data?.transcript_current_chunk, data?.transcript_chunks_completed, data?.transcript_chunks_total, data?.transcript_processing_started_at]);

  const transcriptGenSeconds = useMemo(() => {
    if (!data?.processing_started_at || !data?.processing_completed_at) return null;
    return Math.max(0, Math.round((new Date(data.processing_completed_at) - new Date(data.processing_started_at)) / 1000));
  }, [data]);

  // Load report status
  const loadReportStatus = useCallback(async () => {
    if (!id || !user) return;

    try {
      setIsLoadingReportStatus(true);
      const status = await reportApi.getSessionReportStatus(id);

      // Simply use the backend status - old reports are deleted, so no need for complex timestamp checking
      setReportStatus(status);
      
      // If report is completed, load the report content and set report_url
      if (status.status === 'COMPLETED') {
        try {
          // First, reload session data to get latest markdown fields
          // This ensures we always use the most recent manually edited content
          const sessionRes = await transcriptApi.getSessionById(id, { compact: true });
          const normalized = normalizeData(sessionRes);
          
          // ALWAYS use markdown fields from session data if they exist (these are the source of truth)
          // Only fall back to report file if session fields are empty
          const sessionAtGlance = normalized?.session_at_glance_md || normalized?.sessionAtGlanceMd || '';
          const fullReport = normalized?.full_report_md || normalized?.fullReportMd || '';
          const speakers = normalized?.speakers_md || normalized?.speakersMd || '';
          const inDepth = normalized?.in_depth_md || normalized?.inDepthMd || '';
          
          // Set markdown fields from session data immediately (these take priority)
          if (sessionAtGlance) {
            setMdSessionAtGlance(sessionAtGlance);
          }
          if (fullReport) {
            setMdFullReport(fullReport);
          }
          if (speakers) {
            setMdSpeakers(speakers);
          }
          if (inDepth) {
            setMdInDepth(inDepth);
          }
          
          const report = await reportApi.getSessionReport(id);

          console.log('[loadReportStatus] Full report response:', report);
          console.log('[loadReportStatus] report.data:', report.data);
          console.log('[loadReportStatus] report.data keys:', report.data ? Object.keys(report.data) : 'no data');
          console.log('[loadReportStatus] report.data.Speakers:', report.data?.Speakers);
          console.log('[loadReportStatus] report.data type:', typeof report.data);
          
          // Check if report.data is the data object or if it's nested
          const reportDataToStore = report.data?.data || report.data;
          console.log('[loadReportStatus] reportDataToStore:', reportDataToStore);
          console.log('[loadReportStatus] reportDataToStore.Speakers:', reportDataToStore?.Speakers);
          
          setReportData(reportDataToStore); // Store report data for display
          
          // Extract transcript references for citation linking
          if (reportDataToStore?.transcript_references) {
            setTranscriptReferences(reportDataToStore.transcript_references);
          }
          
          // Extract source references for RAG citations
          if (reportDataToStore?.source_references) {
            setSourceReferences(reportDataToStore.source_references);
          }
          if (reportDataToStore?.related_resources && Array.isArray(reportDataToStore.related_resources)) {
            setRelatedResources(reportDataToStore.related_resources);
          }
          if (reportDataToStore?.factual_notes && Array.isArray(reportDataToStore.factual_notes)) {
            setFactualNotes(reportDataToStore.factual_notes);
          }
          
          // Only populate from report file if session markdown fields are empty
          // Session markdown fields are the source of truth - they override report file data
          if (report.data && report.data['Session report']) {
            console.log('[loadReportStatus] Found Session report section');
            const sessionReport = report.data['Session report'];
            
            // Populate "Session at a glance" from report file ONLY if session field is empty
            if (!sessionAtGlance && sessionReport['Session at a glance']) {
              console.log('[loadReportStatus] Session at a glance not in session, populating from report');
              const atGlance = sessionReport['Session at a glance'];
              let atGlanceMarkdown = '';
              
              if (atGlance.Summary) {
                atGlanceMarkdown += `## Summary\n\n${atGlance.Summary}\n\n`;
              }
              if (atGlance.Keypoints) {
                atGlanceMarkdown += `## Keypoints\n\n${atGlance.Keypoints}\n\n`;
              }
              if (atGlance.Speakers) {
                atGlanceMarkdown += `## Speakers\n\n${atGlance.Speakers}\n\n`;
              }
              
              if (atGlanceMarkdown) {
                console.log('[loadReportStatus] Setting mdSessionAtGlance from report, length:', atGlanceMarkdown.length);
                setMdSessionAtGlance(atGlanceMarkdown.trim());
              }
            } else if (sessionAtGlance) {
              console.log('[loadReportStatus] Using session_at_glance_md from session (source of truth)');
            }
            
            // Populate "Full session report" from report file ONLY if session field is empty
            if (!fullReport && sessionReport['Full session report']) {
              console.log('[loadReportStatus] Full report not in session, populating from report, length:', sessionReport['Full session report'].length);
              setMdFullReport(sessionReport['Full session report']);
            } else if (fullReport) {
              console.log('[loadReportStatus] Using full_report_md from session (source of truth)');
            }
            
            // Populate "Session transcript (report excerpt)" - this is always from report file
            if (sessionReport['Session transcript']) {
              console.log('[loadReportStatus] Found Session transcript, length:', sessionReport['Session transcript'].length);
              setMdTranscript(sessionReport['Session transcript']);
            }
          }
          
          // Populate Speakers tab from report file ONLY if session field is empty
          if (!speakers && report.data && report.data.Speakers) {
            console.log('[loadReportStatus] Speakers not in session, populating from report');
            const speakersData = report.data.Speakers;
            let speakersMarkdown = '';
            
            Object.keys(speakersData).forEach(speakerName => {
              const speakerData = speakersData[speakerName];
              speakersMarkdown += `## ${speakerName}\n\n`;
              
              Object.keys(speakerData).forEach(argumentName => {
                const argumentData = speakerData[argumentName];
                speakersMarkdown += `### ${argumentName}\n\n`;
                
                if (argumentData.Explanation) {
                  speakersMarkdown += `**Explanation:** ${argumentData.Explanation}\n\n`;
                }
                if (argumentData.Evidence) {
                  speakersMarkdown += `**Evidence:** ${argumentData.Evidence}\n\n`;
                }
                if (argumentData.External_evidence) {
                  speakersMarkdown += `**External Evidence (Knowledge Base):** ${argumentData.External_evidence}\n\n`;
                }
                if (argumentData['Major discussion point']) {
                  speakersMarkdown += `**Major discussion point:** ${argumentData['Major discussion point']}\n\n`;
                }
                if (argumentData['Agreed with']) {
                  speakersMarkdown += `**Agreed with:** ${Array.isArray(argumentData['Agreed with']) ? argumentData['Agreed with'].join(', ') : argumentData['Agreed with']}\n\n`;
                }
                if (argumentData['Agreed on']) {
                  speakersMarkdown += `**Agreed on:** ${argumentData['Agreed on']}\n\n`;
                }
                if (argumentData['Disagreed with']) {
                  speakersMarkdown += `**Disagreed with:** ${Array.isArray(argumentData['Disagreed with']) ? argumentData['Disagreed with'].join(', ') : argumentData['Disagreed with']}\n\n`;
                }
                if (argumentData['Disagreed on']) {
                  speakersMarkdown += `**Disagreed on:** ${argumentData['Disagreed on']}\n\n`;
                }
                
                speakersMarkdown += '\n';
              });
              
              speakersMarkdown += '\n';
            });
            
            if (speakersMarkdown) {
              console.log('[loadReportStatus] Setting mdSpeakers from report, length:', speakersMarkdown.length);
              setMdSpeakers(speakersMarkdown.trim());
            }
          } else if (speakers) {
            console.log('[loadReportStatus] Using speakers_md from session (source of truth)');
          }
          
          // Populate In-depth analysis from report file ONLY if session field is empty
          if (!inDepth && report.data && report.data['Additional analysis']) {
            console.log('[loadReportStatus] In-depth not in session, populating from report');
            // Parse additional analysis if needed
            // For now, just log that we found it
          } else if (inDepth) {
            console.log('[loadReportStatus] Using in_depth_md from session (source of truth)');
          }
          
          // Set report_url for frontend display
          if (report.path) {
            // Construct URL to access report
            const reportUrl = `/api/reports/sessions/${id}/report`;
            setData(prev => prev ? { ...prev, report_url: reportUrl } : null);
          }
          
          // Set knowledge graph URL from report
          if (report.graph_path) {
            console.log('[loadReportStatus] Found graph_path in report:', report.graph_path);
            setKnowledgeGraphUrl(report.graph_path);
          } else if (reportDataToStore?.graph_path) {
            console.log('[loadReportStatus] Found graph_path in report data:', reportDataToStore.graph_path);
            setKnowledgeGraphUrl(reportDataToStore.graph_path);
          } else {
            console.log('[loadReportStatus] No graph_path found, keeping placeholder');
            // Keep the default placeholder if no graph_path is available
          }
        } catch (e) {
          console.error('Failed to load report content:', e);
          setReportData(null);
        }
      } else {
        setReportData(null); // Clear report data if not completed
      }
    } catch (e) {
      // Report might not exist yet, which is OK
      if (e.response?.status !== 404) {
        console.error('Failed to load report status:', e);
      }
      setReportStatus(null);
    } finally {
      setIsLoadingReportStatus(false);
    }
  }, [id, user]);

  // Auto-refresh polling when report is processing or pending
  useEffect(() => {
    if (!autoRefreshReport || !reportStatus) return;
    if (reportStatus.status !== 'PROCESSING' && reportStatus.status !== 'PENDING') return;
    
    const interval = setInterval(() => {
      loadReportStatus();
    }, 3000); // Poll every 3 seconds
    
    return () => clearInterval(interval);
  }, [autoRefreshReport, reportStatus?.status, loadReportStatus]);

  // Handler for starting transcript generation
  const handleStartTranscript = async () => {
    setIsGenerating(true);
    try {
      await transcriptApi.startTranscriptGeneration(id, selectedProvider);
      setNotif({
        type: 'success',
        title: 'Transcript generation started',
        message: `Using ${TRANSCRIPT_PROVIDERS.find(p => p.value === selectedProvider)?.label || selectedProvider} provider`
      });
      // Reload session data to show updated status
      setTimeout(() => {
        loadSessionData();
      }, 2000);
    } catch (e) {
      setNotif({ type: 'error', title: 'Failed to start transcript', message: e.message });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateClick = () => {
    setShowRegenerateModal(true);
  };

  const handleConfirmRegenerate = async () => {
    setIsRegenerating(true);
    try {
      // Step 1: Delete old transcript
      await transcriptApi.regenerateTranscript(id);
      
      // Step 2: Start new generation with selected provider
      await transcriptApi.startTranscriptGeneration(id, selectedProvider);
      
      setNotif({
        type: 'success',
        title: 'Transcript regeneration started',
        message: `Old transcript deleted. Generating new one with ${TRANSCRIPT_PROVIDERS.find(p => p.value === selectedProvider)?.label || selectedProvider}`
      });
      
      // Close modal and reload data
      setShowRegenerateModal(false);
      
      // Reload session to show updated status
      setTimeout(() => {
        loadSessionData();
      }, 1500);
    } catch (e) {
      setNotif({ 
        type: 'error', 
        title: 'Failed to regenerate transcript', 
        message: e.response?.data?.detail || e.message 
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDownloadMedia = async () => {
    setIsDownloading(true);
    try {
      await transcriptApi.downloadSessionMedia(id);
      
      const filename = data.original_filename || `session_${id}_media`;
      setNotif({
        type: 'success',
        title: 'Download started',
        message: `Downloading ${filename}`
      });
    } catch (e) {
      console.error('Download error:', e);
      setNotif({ 
        type: 'error', 
        title: 'Failed to download media', 
        message: e.message || e.response?.data?.detail || 'Media file not available'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      // Discover which languages have a completed report/translation.
      let completedLanguages = ['en'];
      try {
        const langResp = await reportApi.getReportLanguages(id);
        const all = Array.isArray(langResp?.languages) ? langResp.languages : [];
        const completed = all
          .filter((l) => l && l.code && String(l.status).toUpperCase() === 'COMPLETED')
          .map((l) => l.code);
        if (completed.length > 0) {
          completedLanguages = completed;
        }
      } catch (langErr) {
        // If the languages endpoint fails, fall back to English-only download.
        console.warn('Could not fetch report languages, downloading English only:', langErr);
      }

      if (completedLanguages.length <= 1) {
        // Single language: just download the PDF directly.
        await reportApi.downloadSessionReportPdf(id, null, completedLanguages[0]);
        setNotif({
          type: 'success',
          title: 'PDF Downloaded',
          message: 'Report has been downloaded as PDF',
        });
        return;
      }

      // Multi-language: ask the backend to ZIP all PDFs into one file.
      // This avoids the browser's "multiple automatic downloads" prompt
      // which was silently dropping the first (English) PDF when we kicked
      // off three sequential downloads from JavaScript.
      const { included, failed } = await reportApi.downloadSessionReportPdfBundle(
        id,
        completedLanguages,
      );

      if (failed.length === 0) {
        setNotif({
          type: 'success',
          title: 'PDFs Downloaded',
          message: `Downloaded ZIP with ${included.length} PDFs (${included.join(', ')})`,
        });
      } else if (included.length > 0) {
        setNotif({
          type: 'warning',
          title: 'Partial download',
          message: `Downloaded ${included.length} PDFs (${included.join(', ')}). Failed for: ${failed.join(', ')}`,
        });
      } else {
        throw new Error(`Could not generate PDFs for: ${failed.join(', ')}`);
      }
    } catch (e) {
      console.error('PDF download error:', e);
      setNotif({
        type: 'error',
        title: 'Failed to download PDF',
        message: e.message || e.response?.data?.detail || 'Could not generate PDF',
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleFixSectionsClick = async () => {
    setShowFixSectionsModal(true);
    setIsDetectingGaps(true);
    setDetectedGaps(null);
    try {
      const result = await transcriptApi.detectTranscriptGaps(id, 30);
      setDetectedGaps(result);
    } catch (e) {
      setNotif({ type: 'error', title: 'Failed to detect gaps', message: e.message });
      setDetectedGaps({ gaps: [], gap_count: 0 });
    } finally {
      setIsDetectingGaps(false);
    }
  };

  const pollTaskStatus = async (taskId, gap, idx) => {
    const maxAttempts = 60;
    const intervalMs = 5000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(r => setTimeout(r, intervalMs));
      try {
        const status = await transcriptApi.getTaskStatus(taskId);
        if (status.ready) {
          if (status.error) {
            setDetectedGaps(prev => {
              if (!prev) return prev;
              const updated = { ...prev, gaps: [...prev.gaps] };
              updated.gaps[idx] = { ...updated.gaps[idx], _status: 'failed' };
              return updated;
            });
            setNotif({ type: 'error', title: 'Section regeneration failed', message: status.error?.slice(0, 200) });
          } else {
            const newCount = status.result?.new_segments_count ?? 0;
            setDetectedGaps(prev => {
              if (!prev) return prev;
              const updated = { ...prev, gaps: [...prev.gaps] };
              updated.gaps[idx] = { ...updated.gaps[idx], _status: 'done' };
              return updated;
            });
            setNotif({
              type: 'success',
              title: 'Section regenerated successfully',
              message: `${formatGapTime(gap.start_time)} - ${formatGapTime(gap.end_time)}: ${newCount} new segments added`
            });
          }
          return;
        }
      } catch {
        // Network blip — keep polling
      }
    }
    setNotif({ type: 'warning', title: 'Regeneration timeout', message: 'Task is still running. Check back later.' });
  };

  const handleRegenerateSection = async (gap, idx) => {
    setRegeneratingSectionIdx(idx);
    try {
      const result = await transcriptApi.regenerateSection(
        id,
        gap.start_time,
        gap.end_time,
        null,
        'en'
      );
      setNotif({
        type: 'info',
        title: 'Section regeneration started',
        message: `Regenerating ${formatGapTime(gap.start_time)} - ${formatGapTime(gap.end_time)}...`
      });
      setDetectedGaps(prev => {
        if (!prev) return prev;
        const updated = { ...prev, gaps: [...prev.gaps] };
        updated.gaps[idx] = { ...updated.gaps[idx], _status: 'processing' };
        return updated;
      });
      if (result.task_id) {
        pollTaskStatus(result.task_id, gap, idx);
      }
    } catch (e) {
      setNotif({ type: 'error', title: 'Failed to start section regeneration', message: e.response?.data?.detail || e.message });
    } finally {
      setRegeneratingSectionIdx(null);
    }
  };

  const handleRegenerateAll = async () => {
    if (!detectedGaps?.gaps?.length) return;
    setIsRegeneratingAll(true);

    setDetectedGaps(prev => {
      if (!prev) return prev;
      const updated = { ...prev, gaps: prev.gaps.map(g => ({ ...g, _status: 'processing' })) };
      return updated;
    });

    try {
      const result = await transcriptApi.regenerateAllSections(id, detectedGaps.gaps);
      setNotif({
        type: 'info',
        title: 'Batch regeneration started',
        message: `Processing ${detectedGaps.gaps.length} gaps sequentially...`
      });

      if (result.task_id) {
        const maxAttempts = 120;
        const intervalMs = 5000;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          await new Promise(r => setTimeout(r, intervalMs));
          try {
            const status = await transcriptApi.getTaskStatus(result.task_id);
            if (status.ready) {
              if (status.error) {
                setNotif({ type: 'error', title: 'Batch regeneration failed', message: status.error?.slice(0, 200) });
                setDetectedGaps(prev => {
                  if (!prev) return prev;
                  return { ...prev, gaps: prev.gaps.map(g => ({ ...g, _status: 'failed' })) };
                });
              } else {
                const r = status.result || {};
                setNotif({
                  type: 'success',
                  title: 'All sections regenerated',
                  message: `${r.succeeded}/${r.total_gaps} gaps fixed, ${r.total_new_segments} new segments added`
                });
                setDetectedGaps(prev => {
                  if (!prev) return prev;
                  const batchResults = r.results || [];
                  return {
                    ...prev,
                    gaps: prev.gaps.map((g, i) => ({
                      ...g,
                      _status: batchResults[i]?.status === 'success' ? 'done' : 'failed'
                    }))
                  };
                });
              }
              setIsRegeneratingAll(false);
              return;
            }
          } catch { /* keep polling */ }
        }
        setNotif({ type: 'warning', title: 'Timeout', message: 'Batch task is still running. Check back later.' });
      }
    } catch (e) {
      setNotif({ type: 'error', title: 'Failed to start batch regeneration', message: e.response?.data?.detail || e.message });
      setDetectedGaps(prev => {
        if (!prev) return prev;
        return { ...prev, gaps: prev.gaps.map(g => ({ ...g, _status: undefined })) };
      });
    }
    setIsRegeneratingAll(false);
  };

  const formatGapTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleGenerateReport = async (config = null) => {
    setIsGeneratingReport(true);
    try {
      // If regenerating (report exists), delete old report data first
      const isRegenerating = reportStatus?.status === 'COMPLETED' || data.report_url;
      
      if (isRegenerating) {
        // Step 1: Delete old report (similar to transcript regeneration)
        await reportApi.regenerateReport(id);
        
        // Clear local state
        setReportData(null);
        setData(prev => prev ? { ...prev, report_url: null } : null);
        // Clear markdown fields when regenerating
        setMdSessionAtGlance('');
        setMdFullReport('');
        setMdTranscript('');
      }
      
      // Ensure auto-refresh is enabled
      setAutoRefreshReport(true);
      
      // Build options object
      const options = {};
      if (config?.llm_model) {
        options.llm_model = config.llm_model;
      }
      if (config?.custom_prompts) {
        options.custom_prompts = config.custom_prompts;
      }
      
      // Step 2: Start new generation
      const response = await reportApi.generateSessionReport(id, options);
      
      setNotif({
        type: 'success',
        title: isRegenerating ? 'Report regeneration started' : 'Report generation started',
        message: `Old report deleted. ${isRegenerating ? 'Generating new report' : 'Report generation has been queued'}. Status: ${response.status || 'QUEUED'}`
      });
      
      // Reload session data and report status
      loadSessionData();
      setTimeout(() => {
        loadReportStatus();
      }, 1500);
      
      // Enable buttons - status will be updated by polling
      setIsGeneratingReport(false);
    } catch (e) {
      setNotif({ 
        type: 'error', 
        title: 'Failed to generate report', 
        message: e.response?.data?.detail || e.message 
      });
      setIsGeneratingReport(false);
      // Reset status on error
      setReportStatus(prev => prev ? { ...prev, status: 'FAILED' } : null);
    }
  };

  const handleReportConfigSubmit = async (customPrompts, llmModel) => {
    const config = {
      custom_prompts: customPrompts,
      llm_model: llmModel
    };
    // Wait for report generation to start before closing modal
    await handleGenerateReport(config);
    // Close modal after generation is initiated
    setShowReportConfigModal(false);
  };

  // Collect all available publish destinations for this session/event
  const getAvailableDestinations = useCallback(() => {
    if (!data) return [];
    const destinations = [];
    if (data.faicon_session_id || data.event_faicon_id) {
      destinations.push('faicon');
    }
    if (data.wp_session_id || data.event_wp_id) {
      destinations.push('wordpress');
    }
    return destinations;
  }, [data]);

  // eslint-disable-next-line no-unused-vars
  const getPublishDestination = useCallback(() => {
    const dests = getAvailableDestinations();
    return dests.length > 0 ? dests[0] : null;
  }, [getAvailableDestinations]);

  const handlePublishClick = (destination) => {
    const dests = getAvailableDestinations();
    const dest = destination || (dests.length === 1 ? dests[0] : null);
    if (!dest) {
      setNotif({
        type: 'error',
        title: 'Cannot publish',
        message: 'This session is not linked to a FAICON or WordPress event. Please link it to an event first.'
      });
      return;
    }
    setPublishDestination(dest);
    setShowPublishModal(true);
  };

  const handleConfirmPublish = async (selectedLang = 'en') => {
    if (!publishDestination || !id) return;
    
    setIsPublishing(true);
    try {
      let result;
      
      if (publishDestination === 'faicon') {
        result = await reportApi.publishToFaicon(id, selectedLang);
      } else {
        result = await reportApi.publishToWordPress(id, selectedLang);
      }
      
      if (result.published_url) {
        // Update session data with published URL
        setData(prev => prev ? { ...prev, url: result.published_url } : null);
        
        // Reload session data to get updated URL
        await loadSessionData();
        
        setNotif({
          type: 'success',
          title: 'Report published successfully',
          message: `Report has been published to ${publishDestination === 'faicon' ? 'FAICON' : 'WordPress'}.`
        });
      } else {
        setNotif({
          type: 'warning',
          title: 'Publish completed',
          message: 'Report was published but no URL was returned.'
        });
      }
      
      setShowPublishModal(false);
    } catch (e) {
      setNotif({
        type: 'error',
        title: 'Failed to publish report',
        message: e.response?.data?.detail || e.message || 'An error occurred while publishing the report.'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUpdatePublish = (destination) => {
    const dests = getAvailableDestinations();
    const dest = destination || (dests.length === 1 ? dests[0] : null);
    if (!dest) {
      setNotif({
        type: 'error',
        title: 'Cannot update publish',
        message: 'This session is not linked to a FAICON or WordPress event.'
      });
      return;
    }
    setPublishDestination(dest);
    setShowPublishModal(true);
  };

  const toggleSpeaker = (speakerName) => {
    setExpandedSpeakers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(speakerName)) {
        newSet.delete(speakerName);
      } else {
        newSet.add(speakerName);
      }
      return newSet;
    });
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await transcriptApi.deleteSession(id);
      
      setNotif({
        type: 'success',
        title: 'Session deleted',
        message: 'Session has been permanently deleted'
      });
      
      // Close modal and redirect to sessions list after a short delay
      setShowDeleteModal(false);
      setTimeout(() => {
        navigate('/sessions');
      }, 1500);
    } catch (e) {
      setNotif({ 
        type: 'error', 
        title: 'Failed to delete session', 
        message: e.response?.data?.detail || e.message 
      });
      setIsDeleting(false);
    }
  };

  if (!user) {
    return (
      <LoginForm 
        onSSOLogin={getAuth}
        ssoLoading={loading}
        ssoError={error}
      />
    );
  }

  if (!data) {
    return (
      <div className="session-detail-page">
        <div className="session-detail-content">
          {notif ? (
            <div className="session-detail-empty">
              <strong>{notif.title}</strong>
              <span>{notif.message}</span>
            </div>
          ) : (
            <div className="session-detail-empty">Loading session...</div>
          )}
        </div>
      </div>
    );
  }

  const hasReport = data.has_reports || data.report_url || reportStatus?.status === 'COMPLETED';
  const statusClass = data.status === 'COMPLETED'
    ? (hasReport ? 'bg-green-50 border-green-200 text-green-700' : 'bg-blue-50 border-blue-200 text-blue-700')
    : data.status === 'FAILED' ? 'bg-red-50 border-red-200 text-red-700'
    : data.status === 'IN_PROGRESS' ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
    : 'bg-gray-50 border-gray-200 text-gray-700';

  const reportBusy = reportStatus?.status === 'PROCESSING' || reportStatus?.status === 'PENDING';
  const availableDestinations = getAvailableDestinations();
  const reportGenerationSeconds = data.report_generation_seconds ?? reportStatus?.processing_duration;
  const reportModel = reportStatus?.used_model || data.report_model || data.reportModel;
  const providerLabel = data.transcript_provider || data.transcript_model || '—';
  const transcriptReady = data.transcript_status === 'COMPLETED' || transcriptSegments.length > 0;
  const transcriptBusy = data.transcript_status === 'PROCESSING' || data.transcript_status === 'PENDING';
  const canGenerateReport = transcriptReady && !isGeneratingReport && !reportBusy;
  const reportActionLabel = data.report_url || reportStatus?.status === 'COMPLETED' ? 'Regenerate report' : 'Generate report';

  const formatDateTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
  };

  const transcriptWordCount = transcriptSegments.reduce((total, segment) => {
    const text = segment.text || segment.content || segment.transcript || '';
    return total + text.trim().split(/\s+/).filter(Boolean).length;
  }, 0);

  const speakerSummaries = Object.values(transcriptSegments.reduce((acc, segment) => {
    const speaker = segment.speaker || segment.speaker_name || segment.speakerName || 'Unknown speaker';
    if (!acc[speaker]) {
      acc[speaker] = { name: speaker, segments: 0 };
    }
    acc[speaker].segments += 1;
    return acc;
  }, {})).sort((a, b) => b.segments - a.segments);

  const compactValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    return value;
  };

  const detailRows = {
    meta: [
      ['Status', data.status],
      ['Event', data.event_title],
      ['User', data.user_name || data.user_email],
      ['Provider', providerLabel],
      ['Report model', reportModel ? formatReportModelLabel(reportModel) : null],
    ].filter(([, value]) => compactValue(value)),
    processing: [
      ['Started', formatDateTime(data.processing_started_at)],
      ['Completed', formatDateTime(data.processing_completed_at)],
      ['Transcript gen', transcriptGenSeconds != null ? `${transcriptGenSeconds}s` : null],
      ['Report gen', reportGenerationSeconds != null ? `${Math.round(reportGenerationSeconds)}s` : null],
      ['Created', formatDateTime(data.created_at)],
      ['Updated', formatDateTime(data.updated_at)],
    ].filter(([, value]) => compactValue(value)),
    file: [
      ['Filename', data.original_filename],
      ['File ID', data.original_file_id],
      ['Duration', data.duration_seconds ? formatDuration(data.duration_seconds) : null],
      ['Published URL', data.url],
    ].filter(([, value]) => compactValue(value)),
  };

  const formatShortDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };
  const headerDate = formatShortDate(data.created_at);

  return (
    <div className="session-detail-page">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="session-detail-head">
            <div className="session-detail-head-main">
              <h1 className="session-detail-title">{data.title || 'Session'}</h1>
              <div className="session-detail-meta-row">
                <span className={`session-detail-status ${statusClass}`}>{data.status || 'N/A'}</span>
                {headerDate && <span className="session-detail-meta-text">{headerDate}</span>}
                {data.original_filename && (
                  <span className="session-detail-meta-file" title={data.original_filename}>
                    <DocumentTextIcon />
                    <span>{data.original_filename}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="session-detail-actions">
              <button type="button" className="session-btn session-btn-danger" onClick={handleDeleteClick}>
                <TrashIcon />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Notification for FAILED status */}
      {data.status === 'FAILED' && data.error_message && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">Transcript Generation Failed</h3>
                <div className="mt-1 text-sm text-red-700">
                  {data.error_message}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Transcript + Report Tabs */}
        <div className="lg:col-span-2 space-y-6">
          <div className="session-main-tabs">
            <button onClick={() => setActiveTab('transcript')} className={activeTab === 'transcript' ? 'active' : ''}>
              Transcript <span>{transcriptSegments.length}</span>
            </button>
            <button onClick={() => setActiveTab('report')} className={activeTab === 'report' ? 'active' : ''}>
              Session report
            </button>
            <button onClick={() => setActiveTab('speakers')} className={activeTab === 'speakers' ? 'active' : ''}>
              {tLabel('Speakers', reportLang)}
            </button>
            <button onClick={() => setActiveTab('graph')} className={activeTab === 'graph' ? 'active' : ''}>
              Knowledge graph
            </button>
            <button onClick={() => setActiveTab('analysis')} className={activeTab === 'analysis' ? 'active' : ''}>
              In-depth analysis
            </button>
            <button onClick={() => setActiveTab('details')} className={activeTab === 'details' ? 'active' : ''}>
              Details
            </button>
          </div>

          {/* Transcript section (separate from Report tabs) */}
          {activeTab === 'transcript' && (
          <div ref={transcriptContainerRef} className="bg-white rounded-xl shadow-sm border border-gray-200 session-transcript-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Transcript</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">Provider: {data.transcript_provider || '—'}</span>
                {transcriptGenSeconds != null && <span className="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">Gen: {transcriptGenSeconds}s</span>}
                {data.transcript_status === 'COMPLETED' && (
                  <Link to={`/sessions/${id}/edit`} className="inline-flex items-center px-3 py-1.5 rounded-md border bg-white text-gray-700 border-gray-300 hover:bg-gray-50">
                    <PencilSquareIcon className="h-4 w-4 mr-1" />
                    Edit
                  </Link>
                )}
              </div>
            </div>
            <div className="p-5 max-h-[600px] overflow-y-auto">
              <TranscriptDisplay 
                segments={transcriptSegments} 
                loading={loadingTranscript}
                highlightRef={highlightSentence}
                transcriptReferences={transcriptReferences}
              />
            </div>
          </div>
          )}

          {/* Tabs */}
          {activeTab === 'report' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="border-b px-4">
              <div className="flex gap-2 items-center justify-between">
                <div className="session-report-head">
                  <SparklesIcon />
                  <span>Session report</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Report Language Selector */}
                  <div className="relative" ref={reportLangMenuRef}>
                    <button
                      onClick={() => setShowReportLangMenu(!showReportLangMenu)}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm whitespace-nowrap ${isTranslatingReport ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-300 hover:bg-gray-50'}`}
                      title={isTranslatingReport ? 'Translating report...' : 'Report language'}
                    >
                      {isTranslatingReport ? (
                        <svg className="animate-spin h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <LanguageIcon className="h-4 w-4 text-gray-500" />
                      )}
                      <span className="font-medium">{(reportLang || 'en').toUpperCase()}</span>
                      <ChevronDownIcon className="h-3 w-3 text-gray-400" />
                    </button>
                    {showReportLangMenu && (
                      <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1 max-h-80 overflow-y-auto">
                        {/* Available languages */}
                        {reportLanguages.filter(l => l.status === 'COMPLETED').map(l => (
                          <div key={l.code} className={`flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 ${l.code === reportLang ? 'bg-primary-50' : ''}`}>
                            <button
                              onClick={() => { handleReportLangSwitch(l.code); setShowReportLangMenu(false); }}
                              className={`flex-1 text-left flex items-center gap-2 ${l.code === reportLang ? 'text-primary-700 font-medium' : 'text-gray-700'}`}
                            >
                              <span>{l.name} ({l.code.toUpperCase()})</span>
                              {l.code === reportLang && <CheckCircleIcon className="h-4 w-4 text-primary-600" />}
                              {l.is_original && <span className="text-xs text-gray-400 ml-1">original</span>}
                            </button>
                            {!l.is_original && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteTranslation(l.code); }}
                                className="ml-2 p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                                title={`Delete ${l.name} translation`}
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                        {/* Processing languages */}
                        {reportLanguages.filter(l => l.status === 'PROCESSING' || l.status === 'PENDING').map(l => (
                          <div key={l.code} className="px-4 py-2 text-sm text-gray-400 flex items-center gap-2">
                            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                            <span className="flex-1">{l.name} — translating...</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCancelTranslation(l.code); }}
                              className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                              title={`Cancel ${l.name} translation`}
                            >
                              <XMarkIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                        {/* Separator & translate option */}
                        <div className="border-t border-gray-100 mt-1 pt-1">
                          <div className="px-4 py-1.5 text-xs font-medium text-gray-400 uppercase">Translate to...</div>
                          <div className="px-3 pb-2 flex gap-1 flex-wrap">
                            {Object.entries(LANGUAGE_NAMES)
                              .filter(([code]) => code !== 'en' && !reportLanguages.some(l => l.code === code))
                              .map(([code, name]) => (
                                <button
                                  key={code}
                                  onClick={() => { handleTranslateReport(code); setShowReportLangMenu(false); }}
                                  disabled={isTranslatingReport}
                                  className="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-primary-50 hover:border-primary-300 text-gray-600 hover:text-primary-700 disabled:opacity-50"
                                >
                                  {code.toUpperCase()}
                                </button>
                              ))
                            }
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <Link to={`/sessions/${id}/report/edit${reportLang !== 'en' ? `?lang=${reportLang}` : ''}`} className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm whitespace-nowrap">
                    <PencilSquareIcon className="h-4 w-4 mr-1" />
                    Edit Report
                  </Link>
                </div>
              </div>
            </div>
            <div className="p-5">
              {/* Translation in progress banner */}
              {isTranslatingReport && (
                <div className="mb-4 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-4 flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-indigo-800">
                      Translating report{translatingLangCode ? ` to ${LANGUAGE_NAMES[translatingLangCode] || translatingLangCode.toUpperCase()}` : ''}...
                    </p>
                    <p className="text-xs text-indigo-600 mt-0.5">This may take 1-2 minutes depending on the report length. You can continue browsing.</p>
                  </div>
                  <button
                    onClick={() => handleCancelTranslation(translatingLangCode)}
                    className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 text-red-700 bg-white hover:bg-red-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
                <div className="space-y-6">
                  {/* Show message if no report generated yet */}
                  {!reportData && reportStatus?.status !== 'PROCESSING' && reportStatus?.status !== 'PENDING' && (
                    <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl shadow-sm p-5">
                      <div className="flex items-center justify-between">
                  <div>
                          <h4 className="text-base font-semibold text-yellow-900 mb-1">No report generated yet</h4>
                          <p className="text-sm text-yellow-700">Generate a report to see the content here.</p>
                        </div>
                        {data.transcript_status === 'COMPLETED' && (
                          <button
                            onClick={handleGenerateReport}
                            disabled={isGeneratingReport}
                            className="px-4 py-2 rounded-lg border text-sm bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isGeneratingReport ? 'Generating...' : 'Generate Report'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Session at a glance */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleSection('glance')}
                      className="w-full bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-4 border-b border-gray-200 hover:from-gray-100 hover:to-gray-150/50 transition-colors cursor-pointer text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                            <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">Session at a glance</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Summary, keypoints, and speakers overview</p>
                          </div>
                        </div>
                        <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsedSections.glance ? '' : 'rotate-180'}`} />
                      </div>
                    </button>
                    {!collapsedSections.glance && (
                      <div className="p-6">
                        <ReadMoreText text={mdSessionAtGlance} maxLength={800} className="" transcriptReferences={transcriptReferences} sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                      </div>
                    )}
                  </div>
                  
                  {/* Full session report */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => toggleSection('fullReport')}
                          className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer text-left flex-1 min-w-0"
                        >
                          <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-lg font-semibold text-gray-900">Full session report</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Comprehensive analysis and detailed insights</p>
                          </div>
                          <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${collapsedSections.fullReport ? '' : 'rotate-180'}`} />
                        </button>
                        {/* Download PDF Button */}
                        {reportStatus?.status === 'COMPLETED' && (
                          <button
                            onClick={handleDownloadPdf}
                            disabled={isDownloadingPdf}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-3 flex-shrink-0"
                            title="Download report as PDF"
                          >
                            {isDownloadingPdf ? (
                              <>
                                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                <span>Generating...</span>
                              </>
                            ) : (
                              <>
                                <ArrowDownTrayIcon className="h-4 w-4" />
                                <span>Download PDF</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    {!collapsedSections.fullReport && (
                      <div className="p-6">
                        <ReadMoreText text={mdFullReport} maxLength={1000} className="" transcriptReferences={transcriptReferences} sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                      </div>
                    )}
                  </div>
                  
                  {/* Related Resources from RAG */}
                  {relatedResources && relatedResources.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => toggleSection('resources')}
                        className="w-full bg-gradient-to-r from-teal-50 to-teal-100/50 px-6 py-4 border-b border-teal-200 hover:from-teal-100 hover:to-teal-150/50 transition-colors cursor-pointer text-left"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                              <svg className="h-5 w-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">Related Resources</h4>
                              <p className="text-xs text-gray-500 mt-0.5">Knowledge base sources related to the discussion topics ({relatedResources.length})</p>
                            </div>
                          </div>
                          <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsedSections.resources ? '' : 'rotate-180'}`} />
                        </div>
                      </button>
                      {!collapsedSections.resources && (
                        <div className="p-6">
                          <div className="space-y-3">
                            {relatedResources.map((resource, idx) => (
                              <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100 hover:border-teal-200 hover:bg-teal-50/30 transition-colors">
                                <span className="inline-flex items-center justify-center text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5 mt-0.5 shrink-0" style={{ minWidth: '28px' }}>
                                  S{resource.ref_num}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <a
                                    href={resource.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm font-medium text-teal-700 hover:text-teal-800 hover:underline"
                                  >
                                    {resource.title || 'Untitled'}
                                  </a>
                                  {resource.snippet && (
                                    <ReadMoreText text={resource.snippet} maxLength={150} className="text-xs text-gray-500 mt-1" />
                                  )}
                                  {resource.label && (
                                    <span className="inline-block mt-1 text-[10px] font-medium text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">{resource.label}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Factual Notes from RAG */}
                  {factualNotes && factualNotes.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => toggleSection('factual')}
                        className="w-full bg-gradient-to-r from-amber-50 to-orange-50/50 px-6 py-4 border-b border-amber-200 hover:from-amber-100 hover:to-orange-100/50 transition-colors cursor-pointer text-left"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                              <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">Factual Notes</h4>
                              <p className="text-xs text-gray-500 mt-0.5">Claims verified against the Diplo knowledge base ({factualNotes.length})</p>
                            </div>
                          </div>
                          <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsedSections.factual ? '' : 'rotate-180'}`} />
                        </div>
                      </button>
                      {!collapsedSections.factual && (
                      <div className="p-6">
                        <div className="space-y-3">
                          {factualNotes.map((note, idx) => {
                            const typeConfig = {
                              confirmation: { bg: 'bg-green-50', border: 'border-green-200', icon: '✓', iconBg: 'bg-green-100', iconColor: 'text-green-700', label: 'Confirmed' },
                              context: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'ℹ', iconBg: 'bg-blue-100', iconColor: 'text-blue-700', label: 'Additional Context' },
                              correction: { bg: 'bg-red-50', border: 'border-red-200', icon: '!', iconBg: 'bg-red-100', iconColor: 'text-red-700', label: 'Correction' },
                            };
                            const config = typeConfig[note.type] || typeConfig.context;
                            return (
                              <div key={idx} className={`${config.bg} border ${config.border} rounded-lg p-4`}>
                                <div className="flex items-start gap-3">
                                  <div className={`h-7 w-7 rounded-full ${config.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                    <span className={`text-sm font-bold ${config.iconColor}`}>{config.icon}</span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`text-xs font-semibold ${config.iconColor} uppercase tracking-wide`}>{config.label}</span>
                                      {note.importance && (
                                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                          note.importance === 'high' ? 'bg-red-100 text-red-600' :
                                          note.importance === 'medium' ? 'bg-amber-100 text-amber-600' :
                                          'bg-gray-100 text-gray-500'
                                        }`}>{note.importance}</span>
                                      )}
                                    </div>
                                    {note.claim && (
                                      <p className="text-xs text-gray-500 italic mb-1.5">"{note.claim}"</p>
                                    )}
                                    <ReadMoreText text={note.note || ''} maxLength={300} className="text-sm text-gray-700 leading-relaxed" sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      )}
                    </div>
                  )}
                  
                  {/* Source References Legend */}
                  {sourceReferences && Object.keys(sourceReferences).length > 0 && (
                    <SourceReferencesPanel sourceReferences={sourceReferences} />
                  )}
                </div>
            </div>
          </div>
          )}
              {activeTab === 'speakers' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl px-6 py-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                <div>
                          <h4 className="text-lg font-semibold text-gray-900">Speakers Analysis</h4>
                          <p className="text-xs text-gray-500 mt-0.5">Detailed breakdown of each speaker's arguments and positions</p>
                        </div>
                      </div>
                  </div>
                  </div>
                  
                  <div className="space-y-8">
                      {(() => {
                        let speakersSource = reportData?.Speakers;
                        if (reportLang && reportLang !== 'en' && mdSpeakers) {
                          try { speakersSource = JSON.parse(mdSpeakers); } catch { speakersSource = null; }
                          if (!speakersSource) {
                            return (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <ReadMoreText text={mdSpeakers} maxLength={1500} className="" sourceReferences={sourceReferences} />
                              </div>
                            );
                          }
                        }
                        if (!speakersSource) return null;
                        return Object.keys(speakersSource).map((speakerName, speakerIndex) => {
                          const speakerData = speakersSource[speakerName];
                          const argumentsList = Object.keys(speakerData);
                          
                          const isExpanded = expandedSpeakers.has(speakerName);
                          
                          return (
                            <div key={speakerName} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                              {/* Speaker Header - Clickable */}
                              <button
                                onClick={() => toggleSpeaker(speakerName)}
                                className="w-full bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-5 border-b border-gray-200 hover:from-purple-100 hover:to-indigo-100 transition-all duration-200 text-left cursor-pointer group"
                              >
                                <div className="flex items-center gap-4">
                                  <div className="h-16 w-16 rounded-full bg-purple-100 border-2 border-purple-200 flex items-center justify-center text-purple-700 font-bold text-2xl flex-shrink-0 group-hover:bg-purple-200 transition-colors">
                                    {speakerName.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{speakerName}</h3>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full font-medium">
                                        {argumentsList.length} {argumentsList.length === 1 ? 'argument' : 'arguments'}
                                      </span>
                                      {!isExpanded && (
                                        <span className="text-xs text-gray-500 italic">Click to expand</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0 transform transition-transform group-hover:scale-110">
                                    {isExpanded ? (
                                      <ChevronUpIcon className="h-6 w-6 text-gray-600" />
                                    ) : (
                                      <ChevronDownIcon className="h-6 w-6 text-gray-600" />
                                    )}
                                  </div>
                                </div>
                              </button>
                              
                              {/* Arguments Container - Conditionally Rendered */}
                              {isExpanded && (
                                <div className="p-6 space-y-6 bg-gray-50/50 transition-all duration-300">
                                {argumentsList.map((argumentName, argIndex) => {
                                  const argumentData = speakerData[argumentName];
                                  return (
                                    <React.Fragment key={argumentName}>
                                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                        {/* Argument Header */}
                                        <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
                                          <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2 mb-2">
                                                <div className="h-2 w-2 rounded-full bg-purple-400"></div>
                                                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Argument {argIndex + 1}</span>
                                              </div>
                                              <h4 className="text-lg font-bold text-gray-900 leading-tight">{argumentName}</h4>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Argument Content */}
                                        <div className="p-5 space-y-4">
                                          {argumentData.Explanation && (
                                            <div className="bg-blue-50/50 border-l-2 border-blue-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-blue-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">{tLabel('Explanation', reportLang)}</div>
                                                  <div className="text-sm text-gray-700 leading-relaxed">{argumentData.Explanation}</div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {argumentData.Evidence && (
                                            <div className="bg-green-50/50 border-l-2 border-green-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-green-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">{tLabel('Evidence', reportLang)}</div>
                                                  <ReadMoreText text={argumentData.Evidence} maxLength={500} className="text-sm text-gray-700 leading-relaxed" transcriptReferences={transcriptReferences} sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {argumentData.External_evidence && (
                                            <div className="bg-teal-50/50 border-l-2 border-teal-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-teal-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-teal-700 uppercase mb-2 tracking-wide">External Evidence (Knowledge Base)</div>
                                                  <ReadMoreText text={argumentData.External_evidence} maxLength={300} className="text-sm text-gray-700 leading-relaxed" sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {argumentData['Major discussion point'] && (
                                            <div className="bg-amber-50/50 border-l-2 border-amber-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-amber-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h10m-7 4h7" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">Major Discussion Point</div>
                                                  <div className="text-sm text-gray-700 leading-relaxed font-medium">{argumentData['Major discussion point']}</div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {(argumentData['Agreed with'] || argumentData['Agreed on']) && (
                                            <div className="bg-emerald-50/50 border-l-2 border-emerald-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-emerald-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">Agreements</div>
                                                  {argumentData['Agreed with'] && (
                                                    <div className="text-sm text-gray-700 mb-2">
                                                      <span className="font-semibold">Agreed with:</span> {Array.isArray(argumentData['Agreed with']) ? argumentData['Agreed with'].join(', ') : argumentData['Agreed with']}
                                                    </div>
                                                  )}
                                                  {argumentData['Agreed on'] && (
                                                    <div className="text-sm text-gray-700">
                                                      <span className="font-semibold">Agreed on:</span> {argumentData['Agreed on']}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {(argumentData['Disagreed with'] || argumentData['Disagreed on']) && (
                                            <div className="bg-red-50/50 border-l-2 border-red-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-red-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">Disagreements</div>
                                                  {argumentData['Disagreed with'] && (
                                                    <div className="text-sm text-gray-700 mb-2">
                                                      <span className="font-semibold">Disagreed with:</span> {Array.isArray(argumentData['Disagreed with']) ? argumentData['Disagreed with'].join(', ') : argumentData['Disagreed with']}
                                                    </div>
                                                  )}
                                                  {argumentData['Disagreed on'] && (
                                                    <div className="text-sm text-gray-700">
                                                      <span className="font-semibold">Disagreed on:</span> {argumentData['Disagreed on']}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {argIndex < argumentsList.length - 1 && (
                                        <div className="my-8 flex items-center justify-center">
                                          <div className="flex-1 border-t-2 border-gray-400"></div>
                                          <div className="mx-4 w-2 h-2 rounded-full bg-gray-400"></div>
                                          <div className="flex-1 border-t-2 border-gray-400"></div>
                                        </div>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                </div>
              )}
              {activeTab === 'details' && (
                <div className="session-details-grid">
                  {detailRows.meta.length > 0 && (
                    <div className="session-detail-card">
                      <h3>Meta</h3>
                      <div className="session-kv-list">
                        {detailRows.meta.map(([label, value]) => (
                          <div key={label}><span>{label}</span><strong>{value}</strong></div>
                        ))}
                      </div>
                    </div>
                  )}
                  {detailRows.processing.length > 0 && (
                    <div className="session-detail-card">
                      <h3>Processing</h3>
                      <div className="session-kv-list">
                        {detailRows.processing.map(([label, value]) => (
                          <div key={label}><span>{label}</span><strong>{value}</strong></div>
                        ))}
                      </div>
                    </div>
                  )}
                  {detailRows.file.length > 0 && (
                    <div className="session-detail-card">
                      <h3>File</h3>
                      <div className="session-kv-list">
                        {detailRows.file.map(([label, value]) => (
                          <div key={label}><span>{label}</span><strong>{value}</strong></div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="session-btn session-btn-secondary session-btn-full"
                        onClick={handleDownloadMedia}
                        disabled={isDownloading}
                      >
                        <ArrowDownTrayIcon />
                        {isDownloading ? 'Downloading...' : 'Video / audio'}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {activeTab === 'graph' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden session-graph-panel">
                  <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">Knowledge graph</h3>
                    {knowledgeGraphUrl && knowledgeGraphUrl !== 'https://example.com/graph' && (
                      <a href={knowledgeGraphUrl} target="_blank" rel="noopener noreferrer" className="session-btn session-btn-ghost">
                        <ArrowTopRightOnSquareIcon />
                        Open in new tab
                      </a>
                    )}
                  </div>
                  {knowledgeGraphUrl && knowledgeGraphUrl !== 'https://example.com/graph' ? (
                    <iframe title="knowledge-graph" src={knowledgeGraphUrl} className="w-full session-graph-frame" />
                  ) : (
                    <div className="session-detail-empty" style={{ margin: 16 }}>
                      <strong>No knowledge graph yet.</strong>
                      <span>Generate the session report to build the knowledge graph.</span>
                    </div>
                  )}
                </div>
              )}
              {activeTab === 'analysis' && (
                <div>
                  {(() => {
                    let analysisSource = reportData?.['Additional analysis'];
                    if (reportLang && reportLang !== 'en' && mdInDepth) {
                      try { analysisSource = JSON.parse(mdInDepth); } catch { analysisSource = null; }
                      if (!analysisSource) {
                        return (
                          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="mb-2">
                              <h4 className="text-sm font-semibold text-gray-900">{tLabel('In-Depth Analysis', reportLang)}</h4>
                            </div>
                            <ReadMoreText text={mdInDepth} maxLength={1500} className="" />
                          </div>
                        );
                      }
                    }
                    if (!analysisSource) {
                      return (
                        <div className="session-detail-empty">
                          <strong>No in-depth analysis yet.</strong>
                          <span>Generate the session report to see agreements, disagreements and follow-up questions.</span>
                        </div>
                      );
                    }
                    return (
                    <div className="space-y-6">
                      {(() => {
                        const additionalAnalysis = analysisSource;
                        return (
                          <>
                            {/* Agreements Section */}
                            {additionalAnalysis.Agreements && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">{tLabel('Agreements', reportLang)}</h4>
                                
                                {additionalAnalysis.Agreements['Agreement points'] && (
                                  <div className="mb-6">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-3">{tLabel('Agreement points', reportLang)}</h5>
                                    <div className="space-y-4">
                                      {Object.keys(additionalAnalysis.Agreements['Agreement points']).map((agreementKey) => {
                                        const agreement = additionalAnalysis.Agreements['Agreement points'][agreementKey];
                                        return (
                                          <div key={agreementKey} className="bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4">
                                            <h6 className="font-semibold text-gray-900 mb-2">{agreementKey}</h6>
                                            {agreement.Speakers && (
                                              <p className="text-xs text-gray-600 mb-2">
                                                <span className="font-medium">Speakers:</span> {Array.isArray(agreement.Speakers) ? agreement.Speakers.join(', ') : agreement.Speakers}
                                              </p>
                                            )}
                                            {agreement.Arguments && (
                                              <div className="text-sm text-gray-700 mb-2 whitespace-pre-line">{agreement.Arguments}</div>
                                            )}
                                            {agreement.Summary && (
                                              <ReadMoreText text={agreement.Summary} maxLength={500} className="text-sm text-gray-800 font-medium mt-2" transcriptReferences={transcriptReferences} sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                            )}
                                            {agreement.Policy_context && (
                                              <div className="mt-3 bg-green-100/60 border border-green-200 rounded-lg p-3">
                                                <div className="text-xs font-semibold text-green-800 uppercase mb-1 tracking-wide">Policy Context (Knowledge Base)</div>
                                                <ReadMoreText text={agreement.Policy_context} maxLength={300} className="text-sm text-green-900 leading-relaxed" sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Agreements['Similar viewpoints'] && (
                                  <div className="mb-6">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-3">{tLabel('Similar viewpoints', reportLang)}</h5>
                                    <div className="space-y-3">
                                      {Object.keys(additionalAnalysis.Agreements['Similar viewpoints']).map((viewpointKey) => {
                                        const viewpoint = additionalAnalysis.Agreements['Similar viewpoints'][viewpointKey];
                                        return (
                                          <div key={viewpointKey} className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
                                            <ReadMoreText text={viewpointKey} maxLength={500} className="font-semibold text-gray-900 mb-2" transcriptReferences={transcriptReferences} sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                            {viewpoint.Speakers && (
                                              <p className="text-xs text-gray-600 mb-2">
                                                <span className="font-medium">Speakers:</span> {Array.isArray(viewpoint.Speakers) ? viewpoint.Speakers.join(', ') : viewpoint.Speakers}
                                              </p>
                                            )}
                                            {viewpoint.Arguments && (
                                              <div className="text-sm text-gray-700 whitespace-pre-line">{viewpoint.Arguments}</div>
                                            )}
                                            {viewpoint.Policy_context && (
                                              <div className="mt-3 bg-blue-100/60 border border-blue-200 rounded-lg p-3">
                                                <div className="text-xs font-semibold text-blue-800 uppercase mb-1 tracking-wide">Policy Context (Knowledge Base)</div>
                                                <ReadMoreText text={viewpoint.Policy_context} maxLength={300} className="text-sm text-blue-900 leading-relaxed" sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Agreements['Unexpected consensus'] && (
                                  <div className="mb-6">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-3">{tLabel('Unexpected consensus', reportLang)}</h5>
                                    <div className="space-y-3">
                                      {Object.keys(additionalAnalysis.Agreements['Unexpected consensus']).map((consensusKey) => {
                                        const consensus = additionalAnalysis.Agreements['Unexpected consensus'][consensusKey];
                                        return (
                                          <div key={consensusKey} className="bg-purple-50 border-l-4 border-purple-500 rounded-r-lg p-4">
                                            <h6 className="font-semibold text-gray-900 mb-2">{consensusKey}</h6>
                                            {consensus.Speakers && (
                                              <p className="text-xs text-gray-600 mb-2">
                                                <span className="font-medium">Speakers:</span> {Array.isArray(consensus.Speakers) ? consensus.Speakers.join(', ') : consensus.Speakers}
                                              </p>
                                            )}
                                            {consensus.Arguments && (
                                              <div className="text-sm text-gray-700 mb-2 whitespace-pre-line">{consensus.Arguments}</div>
                                            )}
                                            {consensus.Explanation && (
                                              <ReadMoreText text={consensus.Explanation} maxLength={500} className="text-sm text-gray-800 font-medium mt-2 italic" transcriptReferences={transcriptReferences} sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                            )}
                                            {consensus.Policy_context && (
                                              <div className="mt-3 bg-purple-100/60 border border-purple-200 rounded-lg p-3">
                                                <div className="text-xs font-semibold text-purple-800 uppercase mb-1 tracking-wide">Policy Context (Knowledge Base)</div>
                                                <ReadMoreText text={consensus.Policy_context} maxLength={300} className="text-sm text-purple-900 leading-relaxed" sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Agreements['Overall assessment'] && (
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">{tLabel('Overall assessment', reportLang)}</h5>
                                    {additionalAnalysis.Agreements['Overall assessment'].Summary && (
                                      <p className="text-sm text-gray-700 mb-2">{additionalAnalysis.Agreements['Overall assessment'].Summary}</p>
                                    )}
                                    {additionalAnalysis.Agreements['Overall assessment']['Consensus level'] && (
                                      <p className="text-xs text-gray-600 italic">{additionalAnalysis.Agreements['Overall assessment']['Consensus level']}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Differences Section */}
                            {additionalAnalysis.Differences && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">{tLabel('Differences', reportLang)}</h4>
                                
                                {additionalAnalysis.Differences['Different viewpoints'] && Object.keys(additionalAnalysis.Differences['Different viewpoints']).length > 0 && (
                                  <div className="mb-6">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Different Viewpoints</h5>
                                    <div className="space-y-3">
                                      {Object.keys(additionalAnalysis.Differences['Different viewpoints']).map((diffKey) => {
                                        const diff = additionalAnalysis.Differences['Different viewpoints'][diffKey];
                                        return (
                                          <div key={diffKey} className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4">
                                            <h6 className="font-semibold text-gray-900 mb-2">{diffKey}</h6>
                                            {diff.Speakers && (
                                              <p className="text-xs text-gray-600 mb-2">
                                                <span className="font-medium">Speakers:</span> {Array.isArray(diff.Speakers) ? diff.Speakers.join(', ') : diff.Speakers}
                                              </p>
                                            )}
                                            {diff.Arguments && (
                                              <div className="text-sm text-gray-700 mb-2 whitespace-pre-line">{diff.Arguments}</div>
                                            )}
                                            {diff.Summary && (
                                              <ReadMoreText text={diff.Summary} maxLength={500} className="text-sm text-gray-800 font-medium mt-2" transcriptReferences={transcriptReferences} sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                            )}
                                            {diff.Policy_context && (
                                              <div className="mt-3 bg-red-100/60 border border-red-200 rounded-lg p-3">
                                                <div className="text-xs font-semibold text-red-800 uppercase mb-1 tracking-wide">Policy Context (Knowledge Base)</div>
                                                <ReadMoreText text={diff.Policy_context} maxLength={300} className="text-sm text-red-900 leading-relaxed" sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Differences['Unexpected differences'] && Object.keys(additionalAnalysis.Differences['Unexpected differences']).length > 0 && (
                                  <div className="mb-6">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Unexpected Differences</h5>
                                    <div className="space-y-3">
                                      {Object.keys(additionalAnalysis.Differences['Unexpected differences']).map((udKey) => {
                                        const ud = additionalAnalysis.Differences['Unexpected differences'][udKey];
                                        return (
                                          <div key={udKey} className="bg-orange-50 border-l-4 border-orange-500 rounded-r-lg p-4">
                                            <h6 className="font-semibold text-gray-900 mb-2">{udKey}</h6>
                                            {ud.Speakers && (
                                              <p className="text-xs text-gray-600 mb-2">
                                                <span className="font-medium">Speakers:</span> {Array.isArray(ud.Speakers) ? ud.Speakers.join(', ') : ud.Speakers}
                                              </p>
                                            )}
                                            {ud.Arguments && (
                                              <div className="text-sm text-gray-700 mb-2 whitespace-pre-line">{ud.Arguments}</div>
                                            )}
                                            {ud.Explanation && (
                                              <ReadMoreText text={ud.Explanation} maxLength={500} className="text-sm text-gray-800 font-medium mt-2 italic" transcriptReferences={transcriptReferences} sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                            )}
                                            {ud.Policy_context && (
                                              <div className="mt-3 bg-orange-100/60 border border-orange-200 rounded-lg p-3">
                                                <div className="text-xs font-semibold text-orange-800 uppercase mb-1 tracking-wide">Policy Context (Knowledge Base)</div>
                                                <ReadMoreText text={ud.Policy_context} maxLength={300} className="text-sm text-orange-900 leading-relaxed" sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {additionalAnalysis.Differences['Overall assessment'] && (
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">{tLabel('Overall assessment', reportLang)}</h5>
                                    {additionalAnalysis.Differences['Overall assessment'].Summary && (
                                      <p className="text-sm text-gray-700 mb-2">{additionalAnalysis.Differences['Overall assessment'].Summary}</p>
                                    )}
                                    {additionalAnalysis.Differences['Overall assessment']['Disagreement level'] && (
                                      <p className="text-xs text-gray-600 italic">{additionalAnalysis.Differences['Overall assessment']['Disagreement level']}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Partial Agreements Section */}
                            {additionalAnalysis['Partial agreements'] && additionalAnalysis['Partial agreements']['Partial agreements'] && Object.keys(additionalAnalysis['Partial agreements']['Partial agreements']).length > 0 && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">{tLabel('Partial Agreements', reportLang)}</h4>
                                <div className="space-y-3">
                                  {Object.keys(additionalAnalysis['Partial agreements']['Partial agreements']).map((paKey) => {
                                    const pa = additionalAnalysis['Partial agreements']['Partial agreements'][paKey];
                                    return (
                                      <div key={paKey} className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
                                        <ReadMoreText text={paKey} maxLength={500} className="font-semibold text-gray-900 mb-2" transcriptReferences={transcriptReferences} sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                        {pa.Speakers && (
                                          <p className="text-xs text-gray-600 mb-2">
                                            <span className="font-medium">Speakers:</span> {Array.isArray(pa.Speakers) ? pa.Speakers.join(', ') : pa.Speakers}
                                          </p>
                                        )}
                                        {pa.Arguments && (
                                          <div className="text-sm text-gray-700 whitespace-pre-line">{pa.Arguments}</div>
                                        )}
                                        {pa.Policy_context && (
                                          <div className="mt-3 bg-amber-100/60 border border-amber-200 rounded-lg p-3">
                                            <div className="text-xs font-semibold text-amber-800 uppercase mb-1 tracking-wide">Policy Context (Knowledge Base)</div>
                                            <ReadMoreText text={pa.Policy_context} maxLength={300} className="text-sm text-amber-900 leading-relaxed" sourceReferences={sourceReferences} onCitationClick={handleCitationClick} />
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Takeaways Section */}
                            {additionalAnalysis.Takeaways && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">{tLabel('Key Takeaways', reportLang)}</h4>
                                
                                {additionalAnalysis.Takeaways['Key takeaways'] && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">{tLabel('Key Takeaways', reportLang)}</h5>
                                    <div className="text-sm text-gray-700 whitespace-pre-line bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
                                      {additionalAnalysis.Takeaways['Key takeaways']}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Takeaways['Resolutions and action items'] && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">{tLabel('Resolutions and action items', reportLang)}</h5>
                                    <div className="text-sm text-gray-700 whitespace-pre-line bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4">
                                      {additionalAnalysis.Takeaways['Resolutions and action items']}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Takeaways['Unresolved issues'] && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">{tLabel('Unresolved issues', reportLang)}</h5>
                                    <div className="text-sm text-gray-700 whitespace-pre-line bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-4">
                                      {additionalAnalysis.Takeaways['Unresolved issues']}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Takeaways['Suggested compromises'] && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">{tLabel('Suggested compromises', reportLang)}</h5>
                                    <div className="text-sm text-gray-700 whitespace-pre-line bg-purple-50 border-l-4 border-purple-500 rounded-r-lg p-4">
                                      {additionalAnalysis.Takeaways['Suggested compromises']}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Thought Provoking Comments */}
                            {additionalAnalysis['Thought provoking comments'] && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">{tLabel('Thought-Provoking Comments', reportLang)}</h4>
                                <div className="space-y-4">
                                  {Object.keys(additionalAnalysis['Thought provoking comments']).filter(key => key !== 'Overall assessment').map((commentKey) => {
                                    const comment = additionalAnalysis['Thought provoking comments'][commentKey];
                                    return (
                                      <div key={commentKey} className="bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-4">
                                        <div className="text-sm font-semibold text-gray-900 mb-2 italic">"{commentKey}"</div>
                                        {comment.Speaker && (
                                          <p className="text-xs text-gray-600 mb-2">
                                            <span className="font-medium">Speaker:</span> {comment.Speaker}
                                          </p>
                                        )}
                                        {comment.Reason && (
                                          <div className="text-sm text-gray-700 mb-2">
                                            <span className="font-medium">Reason:</span> {comment.Reason}
                                          </div>
                                        )}
                                        {comment.Impact && (
                                          <div className="text-sm text-gray-700">
                                            <span className="font-medium">Impact:</span> {comment.Impact}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {additionalAnalysis['Thought provoking comments']['Overall assessment'] && (
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                                      <h5 className="text-sm font-semibold text-gray-700 mb-2">{tLabel('Overall assessment', reportLang)}</h5>
                                      <p className="text-sm text-gray-700">{additionalAnalysis['Thought provoking comments']['Overall assessment']}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Follow-up Questions */}
                            {additionalAnalysis['Follow-up questions'] && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">{tLabel('Follow-up Questions', reportLang)}</h4>
                                <div className="space-y-3">
                                  {Object.keys(additionalAnalysis['Follow-up questions']).map((questionKey) => {
                                    const question = additionalAnalysis['Follow-up questions'][questionKey];
                                    return (
                                      <div key={questionKey} className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
                                        <div className="text-sm font-semibold text-gray-900 mb-2">{questionKey}</div>
                                        {question.Speaker && (
                                          <p className="text-xs text-gray-600 mb-2">
                                            <span className="font-medium">Speaker:</span> {question.Speaker}
                                          </p>
                                        )}
                                        {question.Explanation && (
                                          <div className="text-sm text-gray-700">{question.Explanation}</div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    );
                  })()}
                </div>
              )}
            </div>

        {/* Right: contextual side panel */}
        <aside className="session-side">
          {!transcriptReady && !transcriptBusy && (
            <div className="session-detail-card">
              <h3>Transcript</h3>
              <p className="session-side-note">No transcript is available yet. Select a provider and start generation.</p>
              <div className="session-side-actions">
                <label className="session-side-label" htmlFor="session-provider-select">Provider</label>
                <select
                  id="session-provider-select"
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  className="session-side-select"
                >
                  {TRANSCRIPT_PROVIDERS.map((provider) => (
                    <option key={provider.value} value={provider.value}>
                      {provider.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="session-btn session-btn-secondary"
                  onClick={data.transcript_status === 'FAILED' ? handleRegenerateClick : handleStartTranscript}
                  disabled={isGenerating || isRegenerating}
                >
                  {isGenerating || isRegenerating ? <ClockIcon className="animate-spin" /> : <PlayIcon />}
                  {data.transcript_status === 'FAILED' ? 'Retry transcript' : 'Generate transcript'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'report' && transcriptReady && (
            <div className="session-detail-card">
              <h3>Report actions</h3>
              <div className="session-side-actions">
                <button
                  type="button"
                  className="session-btn session-btn-secondary"
                  onClick={handleGenerateReport}
                  disabled={!canGenerateReport}
                >
                  <DocumentTextIcon />
                  {isGeneratingReport ? 'Generating...' : reportActionLabel}
                </button>
                <button
                  type="button"
                  className="session-btn session-btn-ghost"
                  onClick={handleFixSectionsClick}
                  disabled={!transcriptReady}
                >
                  <WrenchScrewdriverIcon /> Fix missing sections
                </button>
                {reportStatus?.status === 'COMPLETED' && (
                  <button type="button" className="session-btn session-btn-ghost" onClick={handleDownloadPdf} disabled={isDownloadingPdf}>
                    {isDownloadingPdf ? <ArrowPathIcon className="animate-spin" /> : <ArrowDownTrayIcon />}
                    {isDownloadingPdf ? 'Preparing PDF...' : 'Download report PDF'}
                  </button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'report' && reportStatus?.status === 'COMPLETED' && availableDestinations.length > 0 && (
            <div className="session-detail-card">
              <h3>Publishing</h3>
              {data.url && (
                <div className="session-published-mini">
                  <span className="session-detail-badge session-detail-badge-published">Published</span>
                  <a href={data.url} target="_blank" rel="noopener noreferrer" title={data.url}>{data.url}</a>
                </div>
              )}
              <div className="session-side-actions">
                {availableDestinations.map((dest) => (
                  <button
                    key={dest}
                    type="button"
                    className="session-btn session-btn-ghost"
                    onClick={() => data.url ? handleUpdatePublish(dest) : handlePublishClick(dest)}
                    disabled={isPublishing}
                  >
                    <ArrowTopRightOnSquareIcon />
                    {isPublishing ? 'Publishing...' : `${data.url ? 'Update' : 'Publish to'} ${dest === 'faicon' ? 'FAICON' : 'WordPress'}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="session-detail-card">
            <h3>Generation</h3>
            <div className="session-kv-list">
              <div><span>Provider</span><strong>{providerLabel}</strong></div>
              {transcriptGenSeconds != null && <div><span>Transcript gen</span><strong>{transcriptGenSeconds}s</strong></div>}
              {transcriptWordCount > 0 && <div><span>Words</span><strong>~ {transcriptWordCount.toLocaleString()}</strong></div>}
              {reportModel && <div><span>Report model</span><strong>{formatReportModelLabel(reportModel)}</strong></div>}
              {reportGenerationSeconds != null && <div><span>Report gen</span><strong>{Math.round(reportGenerationSeconds)}s</strong></div>}
              {data.updated_at && <div><span>Last updated</span><strong>{formatDateTime(data.updated_at)}</strong></div>}
            </div>
            {activeTab === 'transcript' && transcriptReady && (
              <button type="button" className="session-btn session-btn-secondary session-btn-full" onClick={() => navigate(`/sessions/${id}/edit`)}>
                <PencilSquareIcon /> Edit transcript
              </button>
            )}
            {activeTab === 'transcript' && transcriptReady && transcriptSegments.length > 0 && (
              <div className="session-side-actions" style={{ marginTop: 8 }}>
                <button type="button" className="session-btn session-btn-ghost" onClick={handleDownloadTranscriptPdf} disabled={isDownloadingTranscriptPdf}>
                  {isDownloadingTranscriptPdf ? <ArrowPathIcon className="animate-spin" /> : <ArrowDownTrayIcon />}
                  {isDownloadingTranscriptPdf ? 'Preparing PDF...' : 'Download transcript PDF'}
                </button>
              </div>
            )}
            {activeTab === 'transcript' && transcriptReady && (
              <button type="button" className="session-side-link" onClick={handleRegenerateClick}>
                Regenerate transcript
              </button>
            )}
          </div>

          {data.transcript_status && (
            <div className="session-detail-card">
              <h3>Transcript generation</h3>
              <TranscriptStatusDisplay 
                status={data.transcript_status}
                startedAt={data.transcript_processing_started_at}
                completedAt={data.transcript_processing_completed_at}
                duration={data.transcript_processing_duration}
                errorMessage={data.transcript_error_message}
                provider={data.transcript_provider}
                compact={true}
              />
              {(data.transcript_processing_started_at || data.transcript_processing_completed_at) && (
                <div className="session-kv-list" style={{ marginTop: 10 }}>
                  {data.transcript_processing_started_at && (
                    <div>
                      <span>Started</span>
                      <strong>{formatDateTime(data.transcript_processing_started_at)}</strong>
                    </div>
                  )}
                  {data.transcript_processing_completed_at && (
                    <div>
                      <span>Ended</span>
                      <strong>{formatDateTime(data.transcript_processing_completed_at)}</strong>
                    </div>
                  )}
                </div>
              )}
              {data.transcript_status === 'PROCESSING' && (
                <button
                  type="button"
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`session-auto-toggle ${autoRefresh ? 'active' : ''}`}
                >
                  Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
                </button>
              )}
            </div>
          )}

          {activeTab === 'report' && reportStatus && (
            <div className="session-detail-card">
              <h3>Report generation</h3>
              <ReportStatusDisplay 
                status={reportStatus.status}
                startedAt={reportStatus.processing_started_at}
                completedAt={reportStatus.processing_completed_at}
                queuedAt={reportStatus.queued_at}
                duration={reportStatus.processing_duration}
                errorMessage={reportStatus.error_message}
                queuePosition={reportStatus.queue_position}
                estimatedWaitTime={reportStatus.estimated_wait_time}
                currentStep={reportStatus.current_step}
                progress={reportStatus.progress}
                compact={true}
              />
              {(reportStatus.processing_started_at || reportStatus.processing_completed_at) && (
                <div className="session-kv-list" style={{ marginTop: 10 }}>
                  {reportStatus.processing_started_at && (
                    <div>
                      <span>Started</span>
                      <strong>{formatDateTime(reportStatus.processing_started_at)}</strong>
                    </div>
                  )}
                  {reportStatus.processing_completed_at && (
                    <div>
                      <span>Ended</span>
                      <strong>{formatDateTime(reportStatus.processing_completed_at)}</strong>
                    </div>
                  )}
                </div>
              )}
              {reportBusy && (
                <button
                  type="button"
                  onClick={() => setAutoRefreshReport(!autoRefreshReport)}
                  className={`session-auto-toggle ${autoRefreshReport ? 'active' : ''}`}
                >
                  Auto-refresh: {autoRefreshReport ? 'ON' : 'OFF'}
                </button>
              )}
            </div>
          )}

          {speakerSummaries.length > 0 && activeTab === 'transcript' && (
            <div className="session-detail-card">
              <h3>Speakers</h3>
              <div className="session-speaker-mini-list">
                {speakerSummaries.slice(0, 5).map((speaker) => (
                  <button
                    key={speaker.name}
                    type="button"
                    className="session-speaker-mini"
                    onClick={() => setActiveTab('speakers')}
                  >
                    <span>{speaker.name.split(' ').map(part => part[0]).slice(0, 2).join('').toUpperCase()}</span>
                    <strong>{speaker.name}</strong>
                    <em>{speaker.segments} segment{speaker.segments === 1 ? '' : 's'}</em>
                  </button>
                ))}
              </div>
              <button type="button" className="session-side-link" onClick={() => setActiveTab('speakers')}>
                View all {speakerSummaries.length} speakers →
              </button>
            </div>
          )}
        </aside>
        </div>

      {/* Regenerate Transcript Confirmation Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Regenerate Transcript?</h3>
            <div className="mb-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ Warning: This action cannot be undone
                </p>
              </div>
              <p className="text-gray-700 mb-3">
                This will <strong>permanently delete</strong> the existing transcript and immediately start generating a new one.
              </p>
              
              {/* Provider Selection */}
              <div className="mt-4">
                <label htmlFor="regenerate-provider-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Provider for New Transcript
                </label>
                <select
                  id="regenerate-provider-select"
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  disabled={isRegenerating}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                >
                  {TRANSCRIPT_PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {TRANSCRIPT_PROVIDERS.find(p => p.value === selectedProvider)?.description}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRegenerateModal(false)}
                disabled={isRegenerating}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRegenerate}
                disabled={isRegenerating}
                className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
              >
                {isRegenerating ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-5 w-5" />
                    Delete & Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Session Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Delete Session?</h3>
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 font-medium">
                  🗑️ Warning: This action is permanent and cannot be undone
                </p>
              </div>
              <p className="text-gray-700 mb-3">
                This will <strong>permanently delete</strong> the entire session including:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-3">
                <li>All transcripts and their versions</li>
                <li>Generated reports</li>
                <li>All associated metadata</li>
              </ul>
              <p className="text-gray-700 font-semibold">
                Session: <span className="text-gray-900">{data?.title || `Session ${id}`}</span>
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-5 w-5" />
                    Delete Session
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fix Missing Sections Modal */}
      {showFixSectionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Fix Missing Sections</h3>
              <button onClick={() => setShowFixSectionsModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            {isDetectingGaps ? (
              <div className="flex flex-col items-center py-10">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-amber-500 mb-3" />
                <p className="text-gray-600">Analyzing transcript for gaps...</p>
                <p className="text-xs text-gray-400 mt-1">Checking audio for speech in each gap</p>
              </div>
            ) : detectedGaps && detectedGaps.gaps?.length === 0 ? (
              <div className="flex flex-col items-center py-10">
                <CheckCircleIcon className="h-12 w-12 text-green-500 mb-3" />
                <p className="text-gray-700 font-medium">No gaps detected</p>
                <p className="text-sm text-gray-500 mt-1">The transcript appears to be complete</p>
              </div>
            ) : detectedGaps ? (
              <div className="flex-1 overflow-y-auto space-y-3">
                <p className="text-sm text-gray-600 mb-3">
                  Found <strong>{detectedGaps.gap_count}</strong> gap(s):
                  {detectedGaps.possible_missing_count > 0 && (
                    <span className="text-amber-600 ml-1">{detectedGaps.possible_missing_count} likely missing content</span>
                  )}
                  {detectedGaps.silent_pause_count > 0 && (
                    <span className="text-gray-500 ml-1">, {detectedGaps.silent_pause_count} silent pauses</span>
                  )}
                </p>

                {detectedGaps.gaps.length > 1 && (
                  <button
                    onClick={handleRegenerateAll}
                    disabled={isRegeneratingAll}
                    className="w-full mb-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                  >
                    {isRegeneratingAll ? (
                      <>
                        <ArrowPathIcon className="h-5 w-5 animate-spin" />
                        Regenerating all ({detectedGaps.gaps.length} gaps)...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="h-5 w-5" />
                        Regenerate All ({detectedGaps.gaps.length} gaps)
                      </>
                    )}
                  </button>
                )}

                {detectedGaps.gaps.map((gap, idx) => (
                  <div
                    key={idx}
                    className={`border rounded-lg p-4 ${
                      gap.gap_type === 'possible_missing_content'
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          gap.gap_type === 'possible_missing_content'
                            ? 'bg-amber-200 text-amber-800'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {gap.gap_type === 'possible_missing_content' ? 'Likely Missing' : 'Silent Pause'}
                        </span>
                        <span className="text-sm font-medium text-gray-700">
                          {formatGapTime(gap.start_time)} - {formatGapTime(gap.end_time)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {Math.round(gap.duration)}s
                      </span>
                    </div>

                    {gap.audio_energy_ratio !== null && gap.audio_energy_ratio >= 0 && (
                      <div className="mb-2">
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${gap.audio_energy_ratio > 0.5 ? 'bg-amber-500' : gap.audio_energy_ratio > 0.15 ? 'bg-amber-300' : 'bg-gray-300'}`}
                            style={{ width: `${Math.round(gap.audio_energy_ratio * 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Speech: {Math.round(gap.audio_energy_ratio * 100)}%
                        </p>
                      </div>
                    )}

                    {(gap.before_segment_end_text || gap.after_segment_start_text) && (
                      <div className="text-xs text-gray-500 mb-2 space-y-1">
                        {gap.before_segment_end_text && <p className="truncate">...{gap.before_segment_end_text}</p>}
                        {gap.after_segment_start_text && <p className="truncate">{gap.after_segment_start_text}...</p>}
                      </div>
                    )}

                    {gap._status === 'done' ? (
                      <div className="w-full mt-1 inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-100 border border-green-300 text-green-700">
                        <CheckCircleIcon className="h-4 w-4" />
                        Regenerated successfully
                      </div>
                    ) : gap._status === 'failed' ? (
                      <button
                        onClick={() => handleRegenerateSection(gap, idx)}
                        disabled={isRegeneratingAll}
                        className="w-full mt-1 inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-red-100 border border-red-300 text-red-700 hover:bg-red-200 disabled:opacity-50 transition-colors"
                      >
                        <WrenchScrewdriverIcon className="h-4 w-4" />
                        Retry (failed)
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRegenerateSection(gap, idx)}
                        disabled={isRegeneratingAll || regeneratingSectionIdx === idx || gap._status === 'processing'}
                        className={`w-full mt-1 inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          gap._status === 'processing'
                            ? 'bg-blue-100 border border-blue-300 text-blue-700 cursor-default'
                            : gap.gap_type === 'possible_missing_content'
                              ? 'bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50'
                        }`}
                      >
                        {gap._status === 'processing' ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                            Regenerating...
                          </>
                        ) : regeneratingSectionIdx === idx ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                            Starting...
                          </>
                        ) : (
                          <>
                            <WrenchScrewdriverIcon className="h-4 w-4" />
                            Regenerate Section
                          </>
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : null}

            <div className="mt-4 pt-4 border-t">
              <button
                onClick={() => setShowFixSectionsModal(false)}
                className="w-full px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {notif && (
        <Notification
          type={notif.type}
          title={notif.title}
          message={notif.message}
          onClose={() => setNotif(null)}
          duration={5000}
        />
      )}

      {/* Report Configuration Modal */}
      <ReportConfigModal
        isOpen={showReportConfigModal}
        onClose={() => setShowReportConfigModal(false)}
        onSubmit={handleReportConfigSubmit}
      />
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handleConfirmPublish}
        destination={publishDestination}
        availableDestinations={getAvailableDestinations()}
        onDestinationChange={setPublishDestination}
        isUpdate={!!data?.url}
        isPublishing={isPublishing}
        availableLanguages={reportLanguages}
      />
    </div>
  );
}


